import type { Messages } from "../types";

export const en: Messages = {
  app: {
    name: "Phygiatech ERP",
    shortName: "Phygiatech",
    tagline: "Operations management",
  },
  nav: {
    dashboard: "Dashboard",
    orders: "Orders",
    quotes: "Quotes",
    invoices: "Invoices",
    customers: "Customers",
    products: "Products",
    stock: "Stock",
    purchasing: "Purchasing",
    suppliers: "Suppliers",
    sav: "After-sales",
    shipments: "Shipments",
    reports: "Reports",
    settings: "Settings",
    help: "Help",
    more: "More",
    search: "Search",
    sections: {
      sales: "Sales",
      logistics: "Logistics",
      catalog: "Catalog",
      admin: "Administration",
    },
    collapse: "Collapse sidebar",
    customize: {
      open: "Customize the menu",
      hint: "Drag sections and entries into the order you like. Clicking a section's title folds it.",
      reset: "Reset",
      done: "Done",
      moveSection: "Move the {section} section",
      moveItem: "Move {item}",
    },
    expand: "Expand sidebar",
    openMenu: "Open menu",
  },
  common: {
    actions: {
      save: "Save",
      cancel: "Cancel",
      create: "Create",
      edit: "Edit",
      delete: "Delete",
      archive: "Archive",
      restore: "Restore",
      close: "Close",
      confirm: "Confirm",
      retry: "Retry",
      back: "Back",
      search: "Search",
      filter: "Filter",
      clearFilters: "Clear filters",
      export: "Export",
      more: "More actions",
      signOut: "Sign out",
      copy: "Copy",
      add: "Add",
      remove: "Remove",
      reload: "Reload",
    },
    fields: {
      name: "Name",
      email: "Email",
      phone: "Phone",
      notes: "Notes",
      createdAt: "Created",
      updatedAt: "Updated",
      status: "Status",
      type: "Type",
      reference: "Reference",
      address: "Address",
      street: "Address",
      street2: "Address line 2",
      postalCode: "Postal code",
      city: "City",
      country: "Country",
      website: "Website",
    },
    states: {
      loading: "Loading…",
      saving: "Saving…",
      saved: "Saved",
      empty: "Nothing to show yet",
      noResults: "No results",
      noResultsHint: "Try other terms or remove filters.",
      optional: "Optional",
      required: "Required",
      enabled: "Enabled",
      disabled: "Disabled",
      unknown: "Unknown",
      none: "None",
      unset: "Not set",
    },
    table: {
      rows: "{count, plural, =0 {No rows} one {# row} other {# rows}}",
      selected: "{count, plural, one {# selected} other {# selected}}",
      sortAsc: "Sort ascending",
      sortDesc: "Sort descending",
      columns: "Columns",
      selectAll: "Select the rows shown",
      selectRow: "Select {label}",
      pagination: "List pages",
      range: "{from}–{to} of {total}",
      page: "Page {page} / {pages}",
      previous: "Previous",
      next: "Next",
    },
    confirm: {
      title: "Are you sure?",
      irreversible: "This action cannot be undone.",
    },
    theme: {
      label: "Theme",
      light: "Light",
      dark: "Dark",
      system: "System",
    },
    language: {
      label: "Language",
      fr: "Français",
      en: "English",
    },
  },
  errors: {
    generic: "An unexpected error occurred.",
    network: "Cannot reach the server. Check your connection.",
    offline: "You are offline. Displayed data may be out of date.",
    unauthorized: "Your session has expired. Please sign in again.",
    forbidden: "You do not have permission for this action.",
    notFound: "Item not found.",
    validation: "Some fields are invalid.",
    conflict: "This item was changed in the meantime. Reload the page.",
    rateLimited: "Too many attempts. Try again in a moment.",
    codes: {
      "auth.invalid_credentials": "Incorrect email or password.",
      "auth.two_factor_required": "Verification code required.",
      "auth.two_factor_invalid": "Incorrect verification code.",
      "customer.archived": "This customer is archived: restore it before creating a document for it.",
      "customer.contact_not_found": "This contact no longer exists for this customer: reload the card.",
      "customer.not_found": "Customer not found.",
      "supplier.not_found": "Supplier not found.",
      "product.archived": "{sku} is archived: it can no longer be sold.",
      "product.archive_has_stock": "{sku} still has stock: take it out or count it to zero before archiving it.",
      "product.archive_open_order": "{sku} is still to ship on order {order}: it cannot be archived.",
      "product.archive_in_kit": "This article is part of the composition {sku}: remove it from there before archiving it.",
      "product.not_found": "Product not found.",
      "product.kit_self": "A composition cannot contain itself.",
      "product.component_is_kit": "A component must be a physical article, not a composition.",
      "product.kit_used_as_component": "This article enters a composition: it cannot become one.",
      "product.split_needs_two_axes": "This product has no two options (e.g. vest size and cummerbund size) to split.",
      "product.split_inconsistent": "The variants of this product do not all have the same options.",
      "product.split_archived": "This product is archived: restore it before splitting its variants.",
      "product.already_split": "This product is already made of articles.",
      "product.split_serial_tracked": "A serial-tracked product cannot become a composition.",
      "product.split_sku_taken": "The references of the articles to create already exist.",
      "product.attach_web_variant": "This variant comes from the website: it stays with its Shopify product.",
      "product.attach_same": "The variant already belongs to this product.",
      "product.attach_archived": "This product is archived: restore it before attaching a variant to it.",
      "product.attach_vat": "The two products have different VAT rates: align them before attaching the variant.",
      "product.attach_serial":
        "Only one of the two products is tracked by serial number: align them before attaching the variant.",
      "product.attach_axes": "Enter a value for each option of the chosen product.",
      "product.attach_taken": "The chosen product already has a variant with these options.",
      "product.kit_has_stock": "This article has stock: bring it to zero before turning it into a composition.",
      "product.kit_open_orders":
        "Open orders reserve this article: ship them or change their line before changing its composition.",
      "invoice.not_fully_credited":
        "This invoice is not fully covered by issued credit notes: its order cannot be invoiced again.",
      "invoice.already_reinvoiced": "The quantities of this invoice were already put back to invoice.",
      "product.kit_open_purchase":
        "This article is still awaited on a purchase order: receive or close it before turning the article into a composition.",
      "product.sku_taken": "This SKU is already in use.",
      "variant.not_found": "Variant not found.",
      "import.stock_forbidden": "Opening stock and the cost of a stocked article: only for users managing stock.",
      "import.invalid_file": "The file is unreadable or empty.",
      "import.component_unknown": "Unknown component: its reference exists neither in the catalogue nor earlier in the file.",
      "import.supplier_unknown": "Unknown supplier: create it or import suppliers first.",
      "shopify.not_configured": "The Shopify connection is not configured.",
      "shopify.request_failed": "Shopify did not respond correctly.",
      "shopify.refund_order_unknown":
        "Shopify refund of an order the ERP does not have yet: it will be applied when the order arrives.",
      "stock_count.not_found": "Inventory not found.",
      "stock_count.locked": "This inventory is already validated or cancelled.",
      "stock_count.empty": "Enter at least one counted quantity before validating.",
      "stock.kit_has_no_stock": "A composition has no stock of its own: correct, take out or count its components.",
      "stock.serials_required": "This article is tracked by serial number: enter one number per unit.",
      "stock.serial_unknown": "Unknown serial number for this article.",
      "stock.serial_not_in_stock": "This serial number is not in stock.",
      "stock.serial_duplicate": "Duplicate serial number.",
      "stock.serials_count_mismatch": "The number of serial numbers does not match the quantity.",
      "stock.serial_not_shipped_to_order": "This serial number was not shipped on this order.",
      "stock.kit_not_serial_trackable": "A kit, or a kit component, cannot be tracked by serial number.",
      "stock.serial_not_found": "Serial number not found.",
      "sav.reason_unknown": "Unknown reason: pick one from the list.",
      "family.not_found": "Family not found.",
      "settings.serials_mismatch":
        "Some tracked articles have stock without serial numbers: assign them from the product page before enabling tracking.",
      "stock.insufficient": "Not enough stock for this operation.",
      "stock.reserved_for_other_orders":
        "{quantity} unit(s) of {sku} are set aside for other orders. Confirm to take them: they will be released from those orders.",
      "quote.not_found": "Quote not found.",
      "quote.locked": "This quote can no longer be edited.",
      "quote.external": "This quote comes from IABAKO: it can be accepted, not edited in the ERP.",
      "quote.not_sendable": "This quote is declined, cancelled or expired: it can no longer be sent.",
      "quote.empty": "Add at least one line.",
      "order.not_found": "Order not found.",
      "order.locked": "This order can no longer be edited.",
      "order.cancelled": "This order is cancelled: it can no longer be invoiced.",
      "order.empty_selection": "None of the selected articles is left to ship.",
      "order.cancel_invoiced": "This order has issued invoices: issue a credit note on each first, then cancel the order.",
      "order.allocation_exceeds_line": "You cannot set aside more than the line still has to ship.",
      "order.allocation_exceeds_stock":
        "Not enough free stock to set aside {sku}: the rest is already set aside for other orders.",
      "order.nothing_to_invoice": "Everything on this order has already been invoiced.",
      "order.refs_locked":
        "An invoice has already been issued for this order: its administrative references can no longer change.",
      "order.attachment_invalid": "Empty or unreadable file.",
      "order.attachment_too_large":
        "File too large: the purchase order and the signed delivery note may not exceed 3.5 MB together (Chorus Pro deposit limit).",
      "order.no_purchase_order": "No purchase order attached to this order.",
      "order.no_signed_delivery_note": "No signed delivery note attached to this order.",
      "invoice.not_found": "Invoice not found.",
      "invoice.locked": "An issued invoice cannot be edited: issue a credit note.",
      "invoice.empty": "Add at least one line.",
      "invoice.not_issued": "This invoice has not been issued yet.",
      "invoice.credit_exceeds": "The credit note exceeds what is left to credit on the invoice (amount or quantity of a line).",
      "invoice.deposits_disabled": "Deposit invoices are disabled (Settings › Quotes and invoices › Deposits).",
      "invoice.credit_note_locked":
        "A credit note copies the invoice it corrects: delete this draft and issue another one rather than editing it.",
      "invoice.deposit_locked": "A deposit invoice is recreated rather than edited: delete it and ask for another one.",
      "invoice.deposit_unpaid":
        "A deposit issued on this order is not paid yet: record its payment (or cancel it with a credit note) before issuing the final invoice.",
      "invoice.deposit_exceeds": "The deposits would exceed the order's amount.",
      "invoice.deposit_deducted": "This deposit was already taken off a final invoice: it can no longer be cancelled.",
      "invoice.deposit_partial_credit": "A deposit is cancelled as a whole, not in part.",
      "invoice.deposit_already_invoiced": "The order already has its final invoice: no further deposit.",
      "invoice.deposit_shopify": "No deposit on a web order.",
      "invoice.buyer_address_required":
        "An invoice must show the customer's address: complete the billing address on the customer record, then create the draft again.",
      "invoice.public_siret_required":
        "An invoice to a public body needs its SIRET (Chorus Pro cannot route it without): complete the customer record, then create the draft again.",
      "invoice.deposit_after_final":
        "The order already has its final invoice: this deposit can no longer be issued. Delete the draft.",
      "invoice.deposit_draft_pending":
        "A deposit invoice of this order is still a draft: issue or delete it before issuing the final invoice.",
      "invoice.export_too_long": "Period too long: export at most one year at a time.",
      "invoice.external":
        "This document was issued in Shopify (Simple Invoice): its PDF, payments and credit notes are handled in Shopify.",
      "order.invoiced_in_shopify":
        "Website orders are invoiced in Shopify (Simple Invoice): the invoice is recorded here as soon as the order is paid.",
      "payment.exceeds": "The payment exceeds the amount due.",
      "print.executable_missing": "The agent executable is not available on this server: ask your contractor for it.",
      "print.agent_not_found": "Print agent not found.",
      "print.job_not_found": "Print job not found or already handled.",
      "print.no_label": "This shipment has no label.",
      "print.no_route": "No printer is chosen for this document (Settings › Printing).",
      "print.agent_offline":
        "The printing computer is not responding: print from the browser or check that the agent is running.",
      "shipment.not_found": "Shipment not found.",
      "shipment.locked": "This shipment has already left.",
      "shipment.empty": "Select at least one item to ship.",
      "shipment.no_address": "No shipping or billing address: complete the customer or the order.",
      "shipment.laposte_missing_contact":
        "La Poste requires a phone and an e-mail for every recipient: complete the order or the customer.",
      "shipment.exceeds_remaining": "The quantity exceeds what remains to ship.",
      "company.logo_invalid": "Logo rejected: PNG or JPEG image, 2 MB maximum.",
      "company.logo_missing": "No custom logo.",
      "shipment.not_resendable": "Only a parcel that has left can be sent again.",
      "shipment.not_cancellable": "Only a shipment that has left and is not delivered yet can be cancelled.",
      "shipment.already_in_shopify":
        "This shipment is already recorded in Shopify: cancel it in Shopify first, the ERP will follow.",
      "shipment.cancelled": "This shipment is cancelled.",
      "carrier.not_configured": "The carrier is not configured. Enter the tracking number manually.",
      "carrier.failed": "The carrier rejected the label. Check the address and weight.",
      "carrier.label_in_transit":
        "La Poste has already taken this parcel in charge: its Colissimo label is used and can no longer be cancelled.",
      "carrier.check_failed":
        "Colissimo tracking does not answer: the ERP cannot check the parcel has not left. Try again in a moment.",
      "carrier.customs_hs_code_missing":
        "Customs declaration: article {sku} has no HS code. Enter it on its product card, or a default code in Settings › Company.",
      "carrier.customs_value_missing": "Customs declaration: article {sku} has neither a price nor a value to declare.",
      "carrier.customs_weight_missing": "Customs declaration: article {sku} has no weight. Enter it on its product card.",
      "carrier.customs_empty": "Customs declaration: this parcel holds no article to declare.",
      "carrier.customs_return_unsupported":
        "Colissimo only makes return labels to metropolitan France or Monaco: pick a return address there (Settings › After-sales).",
      "carrier.return_invoice_missing":
        "A return from outside the European Union or from overseas is declared against the original invoice: issue the order's invoice first.",
      "carrier.return_parcel_missing":
        "A return from outside the European Union or from overseas is declared against the original parcel: the order has no shipped parcel with a tracking number.",
      "carrier.return_origin_mixed":
        "An international return declares one country of origin for all its articles: make one label per country of origin.",
      "carrier.customs_multi_parcel":
        "A shipment leaving the European Union goes parcel by parcel (one customs declaration each): ship each parcel separately.",
      "email.not_configured": "Email sending is not configured.",
      "email.no_recipient": "No email address for this recipient.",
      "purchase.empty": "Enter at least one received quantity.",
      "purchase.not_found": "Purchase order not found.",
      "purchase.payment_not_found": "This payment no longer exists on this purchase order.",
      "purchase.locked": "This purchase order can no longer be edited.",
      "purchase.receive_draft": "A draft order cannot be received: send it or mark it sent first.",
      "purchase.not_closable": "Only an order with at least part of it received can have its reception closed.",
      "purchase.below_received": "A quantity cannot go below what was already received (nor a received article be removed).",
      "purchase.received_locked": "Part of it was already received: the supplier and currency can no longer change.",
      "purchase.kit_line": "A composition is not bought as such: order its components.",
      "purchase.receipt_exceeds": "The received quantity exceeds the ordered quantity.",
      "sav.not_found": "After-sales case not found.",
      "sav.exceeds_quantity": "The received quantity exceeds the returned quantity.",
      "sav.duplicate_line": "The same case line appears twice: combine the quantities.",
      "sav.exceeds_order_line": "The case declares more units than the order line contains.",
      "sav.exceeds_returnable":
        "More units would come back from the customer than they received on this order (earlier cases included).",
      "sav.already_refunded": "This case has already been refunded.",
      "sav.refund_after_replacement":
        "This case was already settled by an exchange or a replacement. A partial refund is a credit note on the invoice.",
      "sav.nothing_to_undo": "No action to undo on this case (a refund is not undone here).",
      "sav.undo_received": "The return parcel was already received: undo the receipt first.",
      "sav.undo_serials": "This receipt recorded serial numbers: correct it from the stock.",
      "sav.refund_component": "One article of a set is not refunded automatically: issue a credit note from the invoice.",
      "sav.not_in_order": "This article is not part of the chosen order line.",
      "sav.refund_partial_kit":
        "Shopify only refunds a whole set: refund this component alone from the Shopify admin (custom amount).",
      "sav.no_invoice": "No issued invoice to credit for this order.",
      "sav.locked": "This after-sales case is closed.",
      "sav.attachment_invalid": "Empty or unreadable file.",
      "sav.attachment_too_large": "File too large (10 MB maximum).",
      "sav.exchange_not_open": "An exchange is sent from an open case. The return label already exists: send a replacement.",
      "integration.not_configured": "Fill in this integration's required fields first.",
      "integration.invalid_value": "One of the values entered is not valid.",
      "integration.test_failed": "The service refused the connection. Check the credentials.",
      "iabako.login_failed": "IABAKO refused the sign-in. Check the e-mail and password in Settings › Integrations.",
      "iabako.screen_changed": "An IABAKO screen does not look as expected (IABAKO may have changed). Nothing was written.",
      "iabako.write_blocked": "Reading stopped: IABAKO was about to save a change. Nothing was written.",
      "iabako.timeout": "Reading IABAKO took too long. Start it again.",
      "iabako.unexpected": "Reading IABAKO failed. Nothing was written.",
      "iabako.run_not_found": "This IABAKO read no longer exists.",
      "iabako.run_not_ready": "This IABAKO read is not ready or was already applied.",
      "iabako.run_stale": "This preview is too old: read IABAKO again before applying.",
      "iabako.apply_failed": "Applying was refused and fully rolled back. Read IABAKO again.",
      "iabako.mapping_invalid": "The chosen article or customer no longer exists.",
      "iabako.simulation_failed": "The simulation refused this batch. Nothing was written; detail:",
      "assistant.conversation_not_found": "This conversation no longer exists.",
      "preferences.invalid": "Invalid layout: it was not saved.",
      "assistant.widget_failed":
        "The assistant could not build this widget: rephrase the request (what to show, over which period).",
      "assistant.busy": "The assistant is still thinking: wait for its answer.",
      "assistant.no_pending": "No action is waiting for your confirmation.",
      "assistant.model_failed": "The AI model did not answer. Check the OpenAI key in Settings › Integrations. Detail:",
      "assistant.interrupted": "The answer was interrupted (server restart). Ask again.",
      "assistant.too_many_steps": "The assistant stopped after too many steps. Make the request more specific.",
      "assistant.unexpected": "The assistant hit an error. Ask again.",
      "chorus.not_configured": "The Chorus Pro connection is not configured.",
      "chorus.request_failed": "Chorus Pro rejected or did not process the request.",
      "chorus.missing_siret": "The public customer's SIRET is required for Chorus Pro.",
      "chorus.already_deposited": "This invoice is already deposited on Chorus Pro.",
      "chorus.in_progress": "This invoice is being filed on Chorus Pro: wait for the outcome.",
      "inqom.not_configured": "The Inqom API connection is not configured.",
      "inqom.request_failed": "Inqom refused or did not process the request.",
      "inqom.already_deposited": "This document is already deposited at Inqom.",
      "invoice.export_empty": "No issued invoice in the selection.",
      "purchase_document.not_found": "Document not found.",
      "purchase_document.type_invalid": "Only PDF, JPEG and PNG files are accepted.",
      "purchase_document.invalid": "The file is empty or unreadable.",
      "purchase_document.too_large": "The file exceeds 10 MB.",
      "purchase_document.transmitted": "This document is already transmitted or being transmitted.",
      "purchase_document.no_channel": "No channel to the accountant: configure the Inqom API or the collection address.",
      "purchase_document.reception_open":
        "Close the reception first: the supplier's invoice can only be added afterwards, as it goes straight to the accountant.",
      "sav.nothing_to_return": "No item left to return on this case.",
      "user.not_found": "User not found.",
      "user.email_taken": "A user already exists with this email.",
      "user.self": "You cannot change your own role or access.",
      "order.not_archivable": "Only a delivered or cancelled order can be archived.",
      "purchase_document.supplier_mismatch": "This purchase order belongs to another supplier.",
      "purchase_document.credit_exceeds": "The credit notes of this order would exceed its amount.",
      "purchase.below_paid": "The new total would be lower than what is already paid or credited on this order.",
      "purchase.delivery_address_unknown": "This delivery address no longer exists in Settings › Identity: pick another one.",
      "purchase.rate_not_applicable": "An order in euros is received at rate 1.",
      "shipment.resent": "This parcel was already sent again: cancel the resend instead.",
      "shipment.return_via_sav": "A return parcel is closed by the after-sales receipt, which puts the goods back in stock.",
      "shipment.not_shipped": "Only a parcel that has left can be marked delivered.",
      "webhook.invalid_signature": "Invalid webhook signature.",
    },
    field: {
      required: "This field is required.",
      negativeLine: "A negative price is only possible on a free line, without discount.",
      negativeTotal: "Negative lines cannot make the total negative.",
      email: "Invalid email address.",
      tooShort: "{min, plural, one {At least # character} other {At least # characters}}",
      tooLong: "{max, plural, one {At most # character} other {At most # characters}}",
      number: "Invalid number.",
      positive: "Value must be positive.",
      siret: "Invalid SIRET (14 digits).",
      siretKey: "Invalid SIRET: a digit is wrong (check key).",
      vat: "Invalid VAT number.",
      date: "Invalid date.",
      password: "At least 12 characters.",
      passwordMismatch: "The two passwords do not match.",
      passwordUnchanged: "The new password must differ from the current one.",
      dateFuture: "The date cannot be in the future.",
      fileTooLarge: "File too large (10 MB maximum).",
      shareTotal: "The shares must add up to 100%.",
      amountTooLarge: "Amount too large (€10,000,000 at most per amount and per document).",
      duplicateComponent: "This component appears twice.",
      composition: "Unreadable composition: “REF-A + 2 x REF-B”.",
      duplicateLine: "This line appears twice.",
      hsCode: "Invalid HS code (6 to 10 digits).",
      country: "Invalid country code (2 letters, e.g. FR).",
      duplicateSupplier: "This supplier appears twice.",
      duplicate: "This value appears twice.",
      oneDefault: "Only one address can be the default one.",
      blocks: "Each block must appear once.",
      color: "Invalid colour (#RRGGBB format).",
      imageType: "PNG or JPEG image only.",
      validityBeforeIssue: "The validity date cannot be before the quote date.",
      totalMismatch: "Net total + VAT does not match the gross total.",
      dateRange: "The end date is before the start date.",
    },
    pages: {
      notFoundTitle: "Page not found",
      notFoundBody: "This page does not exist or has moved.",
      forbiddenTitle: "Access denied",
      forbiddenBody: "Your role does not give access to this section. Contact an administrator.",
      crashTitle: "Something went wrong",
      crashBody: "The error has been logged. Reload the page to continue.",
      home: "Back to dashboard",
    },
  },
  auth: {
    signInTitle: "Sign in",
    signInSubtitle: "Access the Phygiatech management workspace.",
    email: "Work email",
    password: "Password",
    signIn: "Sign in",
    signingIn: "Signing in…",
    twoFactorTitle: "Two-step verification",
    twoFactorSubtitle: "Enter the 6-digit code from your authenticator app.",
    twoFactorCode: "Verification code",
    verify: "Verify",
    signedOut: "You are signed out.",
    roles: {
      admin: "Administrator",
      sales: "Sales",
      logistics: "Logistics",
      accounting: "Accounting",
    },
  },
  search: {
    placeholder: "Customer, product, SKU, order, invoice, tracking…",
    hint: "Search",
    shortcut: "⌘K",
    groups: {
      documents: "Documents",
      serials: "Serial numbers",
      navigation: "Go to",
      actions: "Quick actions",
      customers: "Customers",
      products: "Products",
      suppliers: "Suppliers",
    },
    empty: "No results for “{query}”",
    actions: {
      newCustomer: "New customer",
      newProduct: "New product",
      newSupplier: "New supplier",
    },
  },
  dashboard: {
    period: {
      month: "This month — {month}",
      year: "Since January {year}",
      revenue: "Revenue excl. VAT",
      margin: "Margin",
      rate: "Margin rate",
      uncosted: "Margin computed without {amount} of sales whose cost is unknown (history, articles without a purchase cost).",
    },
    monthly: { title: "Month by month — {year}" },
    orders: {
      title: "Orders",
      open: "To process",
      urgent: "Urgent",
      ready: "Ready to prepare",
      stockout: "Out of stock",
      remainder: "Remainders to send",
    },
    stock: {
      title: "Stock",
      outOfStock: "Items out of stock",
      lowStock: "Items below threshold",
      value: "Stock value (purchase price)",
    },
    widgets: {
      customize: "Customize",
      done: "Done",
      reset: "Original layout",
      add: "Add a widget",
      addHint: "Bring back a removed block, or describe to the assistant the one you want.",
      addOne: "Add",
      remove: "Remove “{title}”",
      builtIn: "Dashboard blocks",
      allShown: "All the blocks are already shown.",
      aiTitle: "Create a widget with AI",
      aiHint: "Say what you want to see: the assistant picks the data (with your rights) and the presentation.",
      aiPlaceholder: "E.g. “Top 10 customers of the year by revenue”, “Sent quotes awaiting an answer”",
      aiCreate: "Create",
      aiWorking: "The assistant is preparing the widget…",
      created: "“{title}” added to the dashboard.",
      unavailable: "Data unavailable for now.",
      empty: "Nothing to show.",
      defs: {
        month: "Revenue, margin and margin rate of the month.",
        year: "Revenue, margin and margin rate since January.",
        monthly: "Revenue, margin and margin rate month by month.",
        channelsMonth: "This month's revenue by sales channel.",
        channelsYear: "This year's revenue by sales channel.",
        orders: "Orders to process, urgent, ready, out of stock, remainders.",
        stock: "Articles out of stock or below the threshold, stock value.",
        receivables: "Invoices to collect and overdue.",
      },
    },
    receivables: {
      title: "Invoices to collect",
      total: "{count, plural, one {# invoice} other {# invoices}} to collect",
      overdue: "of which overdue",
      due: "Due {date}",
      noDue: "No due date",
      late: "Overdue",
      empty: "Everything is collected.",
      all: "See all unpaid invoices",
    },
    title: "Dashboard",
    greeting: "Hello {name}",
    comingSoon: "Indicators will appear here as modules are enabled.",
    asOf: "Figures as of {date}, issued invoices net of credit notes, excluding VAT",
    opsMeta: "What the team has on today",
    kpis: {
      revenueMonth: "Revenue this month",
      revenueYear: "Revenue {year} to date",
      marginMonth: "Margin this month",
      marginYear: "Margin {year} to date",
      ordersMonth: "Orders this month",
      toInvoice: "Left to invoice",
      overdue: "Overdue invoices",
      purchasesMonth: "Purchases this month",
      stockValue: "Stock value",
    },
    delta: {
      vsPrevMonth: "{delta} vs last month",
      vsLastYear: "{delta} vs last year",
      newVsPrevMonth: "Nothing last month",
      newVsLastYear: "Nothing last year",
    },
    marginRate: "{rate} of revenue",
    toInvoiceOrders: "{count, plural, =0 {No orders} one {# order} other {# orders}}",
    overdueInvoices: "{count, plural, =0 {No invoices} one {# invoice} other {# invoices}}",
    atSaleValue: "{amount} at sale price",
    hero: {
      ytd: "{year} to date",
      margin: "Margin rate this month",
      orders: "{count, plural, =0 {No orders} one {# order} other {# orders}} this month",
    },
    spark: "12-month trend",
    activity: {
      title: "Activity over the last 26 weeks",
      subtitle: "Orders received per day, all channels",
      less: "Less",
      more: "More",
      day: "{date} — {count, plural, =0 {no orders} one {# order} other {# orders}}, {amount}",
      total: "{count, plural, one {# order} other {# orders}} over the period",
      busiest: "Busiest day: {weekday}",
    },
    channelsTitle: "Sales channels this month",
    channelsTitleYear: "Sales channels since January {year}",
    channelsEmpty: "No sales over the period.",
    span: { label: "Period", month: "Month", year: "{year}" },
    flows: {
      title: "Stock bought and resold this month",
      titleYear: "Stock bought and resold in {year}",
      bought: "In (supplier receipts)",
      sold: "Out (customer shipments)",
      units: "{count, plural, =0 {No items} one {# item} other {# items}}",
      soldValues: "{cost} at cost — {revenue} invoiced",
    },
    share: "{share} of revenue",
    byChannel: "Split by channel",
    chart: {
      title: "Revenue excl. VAT, last 12 months",
      current: "Last 12 months",
      previous: "Same month, previous year",
      showTable: "Show table",
      showChart: "Show chart",
      month: "Month",
      empty: "No revenue over the last 12 months yet",
      emptyHint: "The curve draws itself from the first issued invoice.",
      emptyInvoices: "View invoices",
    },
    top: {
      titleYear: "Best sellers {year}",
      emptyYear: "No invoiced sales this year.",
      title: "Best sellers this month",
      empty: "No sales invoiced this month.",
      sold: "{count, plural, one {# sold} other {# sold}}",
      product: "Product",
    },
    alerts: {
      title: "Needs attention",
      allClear: "Nothing to report right now.",
      openOrders: "{count, plural, one {# order to process} other {# orders to process}}",
      stockoutOrders: "{count, plural, one {# order out of stock} other {# orders out of stock}}",
      urgentOrders: "{count, plural, one {# urgent order} other {# urgent orders}}",
      outOfStock: "{count, plural, one {# item out of stock} other {# items out of stock}}",
      lowStock: "{count, plural, one {# item below its reorder point} other {# items below their reorder point}}",
      staleSav:
        "{count, plural, one {# after-sales case open for more than {days} days} other {# after-sales cases open for more than {days} days}}",
      activeSav: "{count, plural, one {# after-sales case in progress} other {# after-sales cases in progress}}",
      overdue: "{count, plural, one {# overdue invoice} other {# overdue invoices}}",
      failedWebhooks: "{count, plural, one {# Shopify order not imported} other {# Shopify orders not imported}}",
      failedJobs: "{count, plural, one {# background task failed} other {# background tasks failed}}",
    },
  },
  reports: {
    title: "Reports",
    chartTop: "Top 10, share of the period total",
    tabs: { sales: "Sales", purchases: "Purchases", reconciliation: "Shopify reconciliation" },
    presets: {
      label: "Period",
      month: "This month",
      lastMonth: "Last month",
      quarter: "This quarter",
      year: "This year",
      lastYear: "Last year",
      custom: "Custom",
    },
    from: "From",
    to: "To",
    groupBy: "Group by",
    groups: { product: "Product", customer: "Customer", month: "Month", channel: "Channel", supplier: "Supplier" },
    channel: "Channel",
    allChannels: "All channels",
    channels: { shopify: "Website", quote: "Quote", manual: "Manual entry", direct: "Direct invoice", legacy: "IABAKO history" },
    columns: {
      quantity: "Qty",
      net: "Revenue",
      cost: "Cost",
      margin: "Margin",
      marginRate: "Margin rate",
      orders: "Orders",
      ordered: "Ordered",
      received: "Received",
      paid: "Paid",
      share: "Share",
    },
    total: "Total",
    export: "Export CSV",
    exportXlsx: "Export to Excel",
    salesNote:
      "Issued invoices minus credit notes, excluding shipping (including the “Frais de port” lines of IABAKO invoices), in euros excl. VAT. Margin uses the average cost frozen on each invoice. What has no known cost (IABAKO and Shopify archives not taken over, articles without a purchase cost) counts in revenue, not in margin.",
    purchasesNote: "Sent supplier orders, converted to euros at the recorded rate.",
    emptyTitle: "No data for this period",
    emptyBody: "Widen the period or remove the channel filter.",
    invalidRange: "The end date is before the start date.",
    fileSales: "sales",
    filePurchases: "purchases",
    reconciliation: {
      intro:
        "The same revenue can be counted several ways. Each method below has a single definition: pick the one that matches the figure you are comparing.",
      scope: { label: "ERP scope", shopify: "Web shop", all: "All channels" },
      methodsTitle: "Calculation methods",
      sources: { official: "Shopify, official", rebuilt: "ERP rebuild", erp: "ERP" },
      formula: "Formula",
      includes: "Includes",
      excludes: "Excludes",
      where: "Where to find it",
      methods: {
        shopifyOfficial: {
          name: "Shopify total sales",
          formula: "Gross sales − discounts − returns + shipping + taxes",
          includes: "Every web order, paid or not, on its order day; an edit or a refund counts on the day it happens.",
          excludes: "Gift cards sold. Sales outside the shop (quotes, IABAKO).",
          where: "Shopify › Analytics › Reports › Total sales over time",
        },
        shopify: {
          name: "Total sales, Shopify method",
          formula: "Gross sales − discounts − returns + shipping + taxes",
          includes:
            "Shopify's sales ledger, copied by the ERP: each order, edit or refund on the day it happened. It must match Shopify's report to the cent.",
          excludes: "Gift cards and test orders. Nothing before the ledger starts: Shopify's API only gives the last 60 days.",
          where: "Computed by the ERP. It is not Shopify's report itself.",
        },
        invoicedGross: {
          name: "Invoiced incl. VAT",
          formula: "Issued invoices − credit notes, VAT included, shipping included",
          includes:
            "Invoices on their issue date (Shopify order: order day; article edited or refunded after the invoice: credit note or supplementary invoice on its own day), plus the Shopify and IABAKO archives.",
          excludes: "Orders not paid yet, hence not invoiced yet. Deposits: the final invoice carries the sale. Drafts.",
          where: "Invoices",
        },
        invoicedNet: {
          name: "Invoiced excl. VAT",
          formula: "Issued invoices − credit notes, VAT excluded, shipping included",
          includes: "The same invoices and credit notes as “Invoiced incl. VAT”, without VAT.",
          excludes: "VAT. Unpaid orders, deposits, drafts.",
          where: "Invoices",
        },
        invoicedGoods: {
          name: "Goods revenue excl. VAT",
          formula: "Issued invoices − credit notes, VAT excluded, shipping excluded",
          includes: "The figure of the sales report and the dashboard: margins are computed on it.",
          excludes: "Shipping, rebilled at cost. VAT. Unpaid orders, deposits, drafts.",
          where: "Reports › Sales, Dashboard",
        },
        collected: {
          name: "Collected incl. VAT",
          formula: "Payments received on invoices, on their date",
          includes: "Recorded payments, including those Shopify collected, on the day Shopify was paid.",
          excludes:
            "Money given back, which credit notes count. Shopify payments recorded before 14 October 2026 carry their import day, not their payment day.",
          where: "Invoices › Payments",
        },
      },
      official: {
        missing_scope:
          "Shopify does not let the ERP read its reports yet. The ERP's app needs the “read_reports” access in Shopify's Dev Dashboard, with level 2 customer data access. Until then, only the rebuild is shown.",
        not_configured: "Shopify is not connected: Settings › Integrations.",
        error: "Shopify's report could not be read. Try again later.",
      },
      shopifyTable: {
        title: "Shopify sales report, month by month",
        month: "Month",
        orders: "Orders",
        gross: "Gross sales",
        discounts: "Discounts",
        returns: "Returns",
        net: "Net sales",
        shipping: "Shipping",
        taxes: "Taxes",
        total: "Total sales",
        official: "Official",
        rebuilt: "Rebuilt",
        gap: "Gap",
        note: "Amounts excl. VAT except “Taxes” and “Total sales”. “Returns” covers articles refunded and articles removed by an order edit.",
      },
      bridge: {
        title: "From Shopify sales to the ERP's invoices",
        intro:
          "For the period, what Shopify counts as total sales and what the ERP invoiced for web orders, VAT included, order by order. Each gap is filed under its cause.",
        shopify: "Shopify total sales (rebuild)",
        erp: "Invoices and credit notes of web orders in the ERP",
        partial:
          "The Shopify ledger starts on {date}. Before that day the Shopify column is incomplete: the API only goes back 60 days.",
        olderOrders:
          "The ledger covers orders placed since {date}. An edit or a refund made in the period on an older order is not in it: without the “read_all_orders” access, Shopify's API only gives the last 60 days of orders. Shopify's official report does count it.",
        noLedger: "Shopify's sales ledger has not been read yet: it fills at the next sync.",
        normal: "Method difference",
        anomaly: "Anomaly",
        order: "Order",
        shopifyColumn: "Shopify",
        erpColumn: "ERP",
        gapColumn: "Gap",
        orders: "{count, plural, one {# order} other {# orders}}",
        none: "No gap over this period: the ERP invoices what Shopify sells.",
        kinds: {
          rounding: {
            label: "VAT rounding",
            help: "The ERP computes VAT once per rate on the invoice total, as French invoices require. Shopify computes it line by line. The gap is a few cents per invoice at most.",
          },
          timing: {
            label: "Date shift",
            help: "The order is fully invoiced, but part of it falls in another month. Shopify counts an edit or a refund on the day it happens; an invoice made before that day counts it on its own date.",
          },
          not_invoiced: {
            label: "Orders not fully paid",
            help: "Shopify counts an order as soon as it is placed. The ERP only invoices it once paid.",
          },
          archive_dates: {
            label: "CSV history dates",
            help: "Orders older than the ERP come from Shopify's CSV export, which does not give refund dates. The invoice is dated on payment, the credit note on payment or cancellation.",
          },
          outside_ledger: {
            label: "Before the Shopify ledger",
            help: "Order older than the ledger. The ERP does not know its Shopify sales in detail, because the API only goes back 60 days.",
          },
          document_mismatch: {
            label: "Invoicing gaps",
            help: "Over the order's whole life, the ERP does not invoice what Shopify sells: a credit note or a supplementary invoice is missing. To be fixed.",
          },
        },
        toGoods: {
          title: "From invoices incl. VAT to the sales report's revenue",
          vat: "VAT",
          shipping: "Shipping excl. VAT",
          goods: "Goods revenue excl. VAT (sales report, dashboard)",
        },
      },
      anomalies: {
        title: "Gap history",
        intro:
          "After each sync, the ERP compares every Shopify order with its invoices. A gap stays open while it exists, then it is marked resolved. This check changes no figure.",
        check: "Check now",
        checked: "Check complete",
        open: "Open",
        closed: "Resolved",
        resolved: "Resolved on {date}",
        since: "Since {date}",
        none: "No gap recorded.",
        amount: "ERP − Shopify",
        kinds: {
          shopify_order_missing: "Shopify order missing from the ERP",
          shopify_invoice_missing: "Paid order without an invoice in the ERP",
          shopify_amount_mismatch: "The ERP does not invoice what Shopify sells",
          shopify_sync_failed: "Shopify order the sync cannot take in",
        },
      },
    },
  },
  pwa: {
    updateAvailable: "A new version is available.",
    updateAction: "Update",
    offlineReady: "The app is ready to work offline.",
  },
  customers: {
    origin: { label: "Origin", shopify: "Website", pro: "Direct" },
    title: "Customers",
    singular: "Customer",
    new: "New customer",
    importShopify: "Import Shopify customers",
    importShopifyDone:
      "{created, plural, =0 {No new customer} one {# customer imported} other {# customers imported}}, {linked, plural, =0 {none matched} one {# matched} other {# matched}}",
    searchPlaceholder: "Name, email, city, SIRET…",
    emptyTitle: "No customers",
    emptyBody: "Create your first customer or import your IABAKO file.",
    created: "Customer created",
    updated: "Customer updated",
    archived: "Customer archived",
    kinds: {
      individual: "Individual",
      business: "Business",
      public: "Public body",
    },
    fields: {
      kind: "Customer type",
      displayName: "Display name",
      companyName: "Company name",
      firstName: "First name",
      lastName: "Last name",
      siret: "SIRET",
      vatNumber: "VAT number",
      chorusServiceCode: "Chorus Pro service code",
      paymentTermsDays: "Payment terms (days)",
      language: "Document language",
      billingAddress: "Billing address",
      shippingAddress: "Shipping address",
      sameAsBilling: "Same as billing address",
      contacts: "Contacts",
      jobTitle: "Job title",
      primaryContact: "Primary contact",
    },
    tabs: {
      overview: "Overview",
      documents: "Documents",
      history: "History",
    },
    activity: {
      archives: "History (IABAKO, Shopify)",
      archived: "Archived",
      legacyKinds: { quote: "Quote (archive)", invoice: "Invoice (archive)", credit_note: "Credit note (archive)" },
      legacySources: { iabako: "IABAKO", shopify: "Shopify" },
      legacyStatuses: {
        paid: "Paid",
        pending: "Pending",
        partially_refunded: "Partially refunded",
        refunded: "Refunded",
        partially_paid: "Partially paid",
        edited: "Edited",
        voided: "Voided",
      },
      legacyCancelled: "cancelled",
      quotes: "Quotes",
      orders: "Orders",
      invoices: "Invoices and credit notes",
      sav: "After-sales",
      receivable: "Outstanding",
      empty: "No documents for this customer.",
      more: "{shown} most recent of {total}",
    },
    noContacts: "No contacts yet.",
    addContact: "Add a contact",
    moreOptions: "Terms and notes",
    contactsCount: "{count, plural, =0 {No contacts} one {# contact} other {# contacts}}",
    since: "Customer since {date}",
    showArchived: "Archived",
    all: "All",
  },
  suppliers: {
    title: "Suppliers",
    purchaseOrders: "Purchase orders",
    singular: "Supplier",
    new: "New supplier",
    searchPlaceholder: "Name, country, email…",
    emptyTitle: "No suppliers",
    emptyBody: "Add your suppliers to link products to them.",
    created: "Supplier created",
    updated: "Supplier updated",
    languageHint: "Language of the purchase orders sent to this supplier (texts in Settings › Documents).",
    languages: { fr: "French", en: "English" },
    fields: {
      language: "Order language",
      currency: "Purchase currency",
      leadTimeDays: "Lead time (days)",
      contactName: "Contact",
      productCount: "Products",
    },
  },
  products: {
    suppliers: {
      title: "Item suppliers",
      hint: "{sku}: each supplier's reference, price and minimum order. The main supplier drives replenishment.",
      open: "Suppliers of {sku}",
      empty: "No supplier recorded for this item.",
      supplier: "Supplier",
      pick: "Choose a supplier",
      cost: "Purchase price ({currency})",
      leadTime: "Lead time (days)",
      minQuantity: "Minimum order",
      label: "Name at this supplier",
      labelHint:
        "Full name of the item on this supplier's purchase orders (its nomenclature, options included). Takes precedence over the product's supplier name.",
      add: "Add a supplier",
      saved: "Suppliers saved",
    },
    title: "Products",
    singular: "Product",
    new: "New product",
    searchPlaceholder: "Name, SKU, type…",
    emptyTitle: "No products",
    emptyBody: "Import the Shopify catalog or create a product manually.",
    created: "Product created",
    updated: "Product updated",
    importShopify: "Import from Shopify",
    importShopifyDone:
      "{created, plural, =0 {No new products} one {# product imported} other {# products imported}}, {updated, plural, =0 {none updated} one {# updated} other {# updated}}",
    channels: {
      web: "Web",
      offline: "Offline",
    },
    fields: {
      serialTracked: "Serial number tracking",
      serialTrackedHint: "Each unit is recorded with its serial number at receipt and picked at shipment.",
      title: "Name",
      productType: "Family",
      description: "Description",
      supplierLabel: "Supplier name",
      supplierLabelHint:
        "Replaces the name on purchase orders on its own, without the options: include them here, or per variant in the Suppliers tab. Empty = the name and the options.",
      hsCode: "HS code (customs)",
      hsCodeHint:
        "6 to 10 digits, declared on the CN23 of a parcel leaving the European Union. Empty = the default code in Settings › Company.",
      originCountry: "Country of origin",
      originCountryHint: "2-letter code (FR, CN…). Empty = the default country in Settings › Company.",
      sku: "SKU",
      ean: "EAN",
      options: "Options",
      weightGrams: "Weight (g)",
      salePrice: "Sale price excl. VAT",
      salePriceTtc: "Sale price incl. VAT",
      costPrice: "Unit cost (WAC)",
      purchasePrice: "Purchase price excl. VAT",
      purchasePriceHint: "Weighted average cost, updated at each receipt",
      costMissing: "Not set",
      vatRate: "VAT",
      margin: "Margin",
      reorderPoint: "Reorder point",
      targetStock: "Target stock",
      mainSupplier: "Main supplier",
      variants: "Variants",
      channel: "Channel",
      shopify: "Shopify",
    },
    variantCount: "{count, plural, one {# variant} other {# variants}}",
    addVariant: "Add a variant",
    costFromLedger: "Average cost kept by receipts",
    costRevalued: "Average cost kept by receipts: changing it revalues the stock",
    shopifyPrice: {
      synced: "Price sent to Shopify",
      pending: "Price waiting to be sent to Shopify",
      pendingOff: "Price changed here, not sent yet: sending prices to Shopify is off (Settings › Integrations)",
      failed: "Shopify refused the price: {error}",
    },
    marginHint: "Margin {rate}",
    addAxis: "Add an option",
    axesHint: "For example Size or Colour. Each variant sets its value for every option.",
    webLocked:
      "Sold on the website: name, family, description, options and weight are managed in Shopify and overwritten on every catalog import. The price is changed here: it is sent to Shopify.",
    priceTtc: "{amount} incl. VAT",
    priceFromShopify: "Website price: changed here, it is sent to Shopify (incl. VAT).",
    priceFrom: "from {amount}",
    allFamilies: "All families",
    importing: "Importing…",
    variantsTitle: "{count, plural, one {# variant} other {# variants}}",
    noSupplier: "No supplier",
    exclVat: "excl. VAT",
    weightFilter: { label: "Weight", all: "All weights", missing: "Missing weight" },
    costFilter: { label: "Purchase cost", all: "All costs", missing: "No purchase cost" },
    attach: {
      title: "Attach to another product",
      description:
        "The variant becomes a variant of the chosen product, with its options (size, colour…). Its stock, movements, supplier prices and history follow it.",
      variant: "Variant",
      target: "Destination product",
      targetPlaceholder: "Search a product…",
      taken: "Already on this product: {values}",
      summary: "{sku} will become « {options} » of « {title} ».",
      summaryArchived: "{sku} will become « {options} » of « {title} ». « {source} », left empty, will be archived.",
      none: "The variants of this product come from the website: they stay with their Shopify product.",
      apply: "Attach",
      done: "{sku} attached to « {title} »",
    },
    split: {
      title: "Split the variants",
      description:
        "Creates one article per option (e.g. the vest by size and the cummerbund by size). Each site variant becomes a composition of these articles: the site still sells the combination, stock is followed article by article and a quote can mix sizes.",
      axisTitle: "Article “{name}”",
      share: "Share (%)",
      shareHint: "Of cost, price and weight",
      valueFigures: "{price} excl. VAT · cost {cost}",
      kits: "Compositions of the site variants",
      stockMoved: "{count} units in stock will move from the site variants to their articles.",
      apply: "Split",
      done: "Variants split",
    },
    composition: {
      title: "Composition",
      hint: "{sku}: physical articles that leave with this variant. Stock, counts and purchases concern the components; the variant keeps its Shopify link and price.",
      open: "Composition of {sku}",
      empty: "Physical article: no component. Add some to turn it into a composition.",
      component: "Component",
      pick: "Search an article by SKU or name…",
      quantity: "Quantity",
      add: "Add a component",
      saved: "Composition saved",
      madeOf: "Made of",
      usedIn: "Enters into",
      part: "{quantity} × {sku}",
      availableOf: "{available} available",
    },
  },
  families: {
    title: "Product families",
    hint: "Families group products in filters, inventories, parcel weights and serial number tracking. The order chosen here is the order of the lists.",
    name: "Family",
    products: "{count, plural, =0 {no product} one {# product} other {# products}}",
    shopify: "{count, plural, one {# Shopify product} other {# Shopify products}}",
    shopifyHint: "Web products take their Shopify type at each import; a rename or merge made here keeps applying to that type.",
    rename: "Rename",
    renameTitle: 'Rename the family "{name}"',
    newName: "New name",
    merge: "Merge",
    mergeTitle: 'Merge into "{name}"?',
    mergeBody: 'The {count} products of "{from}" join "{to}"; the parcel weight of "{to}" is kept.',
    renamed: "Family renamed",
    merged: "Families merged",
    saveOrder: "Save the order",
    orderSaved: "Order saved",
    moveUp: "Move up",
    moveDown: "Move down",
    empty: "No family yet: enter one on a product page.",
    datalistHint: "Pick an existing family or type a new one.",
  },
  stock: {
    reserved: { title: "Take units set aside?", confirm: "Take them" },
    title: "Stock",
    serials: {
      title: "Serial numbers",
      tab: "Serial numbers",
      unit: "Unit {serial}",
      lookup: "Serial number",
      scan: "Scan or type a number, then Enter",
      scanHint: "One number per unit; pasting several lines adds each line.",
      count: "{count} / {expected}",
      pick: "Pick the units",
      pickHint: "Tick the units leaving, or scan their number.",
      inStock: "{count, plural, one {# unit in stock} other {# units in stock}}",
      none: "No unit recorded.",
      statuses: { in_stock: "In stock", shipped: "Shipped", quarantine: "Quarantine", scrapped: "Scrapped", exited: "Exited" },
      events: {
        received: "Received",
        shipped: "Shipped",
        returned: "Return received",
        restocked: "Restocked",
        quarantined: "Quarantined",
        scrapped: "Scrapped",
        exited: "Left stock",
        adjusted: "Adjustment",
        assigned: "Assigned",
        auto_assigned: "Assigned automatically",
        count_found: "Inventory: found",
        count_missing: "Inventory: missing",
      },
      pending: "Numbers assigned automatically, to check",
      pendingHint:
        "This parcel was prepared outside the ERP (web shop): units were picked in receipt order. Correct them if needed.",
      confirm: "Confirm the numbers",
      confirmed: "Serial numbers saved",
      product: "Article",
      status: "Status",
      history: "History",
      links: "Documents",
      receivedWith: "Received with {number}",
      shippedWith: "Left with {number}",
      order: "Order {number}",
      customer: "Customer",
      sav: "After-sales {number}",
      found: "Numbers found",
      missing: "{count, plural, one {# number missing} other {# numbers missing}}",
      existing:
        "{count, plural, one {# unit already in stock: enter its number} other {# units already in stock: enter their numbers}}",
      existingHint: "Tracking starts with the current stock: every unit on the shelf gets its number now.",
      enable: "Enable tracking",
      disable: "Disable tracking",
      enabled: "Serial number tracking enabled",
      disabled: "Serial number tracking disabled",
      searchGroup: "Serial numbers",
    },
    tabs: { levels: "Levels", counts: "Inventories", valuation: "Valuation" },
    levels: { all: "All", out: "Out of stock", low: "Below threshold", ok: "Available" },
    fields: {
      product: "Item",
      onHand: "On hand",
      reserved: "Reserved",
      available: "Available",
      incoming: "Incoming",
      reorderPoint: "Threshold",
      unitCost: "WAC",
      lastCount: "Last inventory",
    },
    thresholdValue: "Reorder threshold: {value}",
    views: { articles: "Physical articles", kits: "Compositions" },
    kit: {
      badge: "Composition",
      hint: "Stock derived from its components: the scarcest one sets the limit. Nothing is corrected or counted here.",
      component: "{quantity} × {sku} — {available} available",
      emptyKits: "No composition",
      emptyKitsBody: "A composition is defined from the product sheet, variant by variant.",
    },
    searchPlaceholder: "Item, SKU, family…",
    emptyTitle: "No items",
    emptyBody: "Import or create products to track their stock.",
    adjust: "Correct stock",
    adjustHint: "Enter what is actually on the shelf: the difference is logged in the history.",
    adjustSerialsAdded: "Serial numbers of the extra units",
    adjustSerialsAddedHint: "New or found units: one number per unit above the stock.",
    adjustSerialsMissing: "Missing units",
    adjustSerialsMissingHint: "Tick the units no longer on the shelf.",
    countedQuantity: "Quantity actually in stock",
    countedOn: "Counted on",
    countedOnHint: "Leave empty if you just counted. Later movements still apply.",
    note: "Reason",
    notePlaceholder: "Recount, breakage, entry error…",
    adjusted: "Stock corrected ({delta})",
    unchanged: "Stock unchanged",
    movements: "Movements",
    noMovements: "No movements for this item.",
    balance: "Balance",
    lastCount: "Last inventory: {date}",
    neverCounted: "Never counted",
    exit: {
      title: "Remove from stock",
      hint: 'Test, trade show, loan, breakage… Items leave stock with no delivery note and no parcel. To send a parcel, use "Ship without order".',
      quantity: "Quantity",
      reason: "Reason",
      note: "Details",
      notePlaceholder: "Trade show name, borrower, circumstances…",
      done: "{count, plural, one {# item} other {# items}} taken out of stock",
      reasons: {
        test: "Test / trial",
        trade_show: "Trade show",
        loan: "Loan",
        loss: "Loss / breakage",
        internal_use: "Internal use",
        other: "Other",
      },
    },
    movementTypes: {
      initial: "Opening stock",
      adjustment: "Correction",
      count: "Inventory",
      purchase_receipt: "Supplier receipt",
      sale_shipment: "Customer shipment",
      sav_return: "After-sales return",
      sav_replacement: "After-sales replacement",
      free_shipment: "Shipment without order",
      resend: "Parcel sent again",
      stock_exit: "Removed from stock",
      shipment_cancel: "Shipment cancelled",
      cost_revaluation: "Cost revaluation",
    },
    counts: {
      new: "New inventory",
      newHint: "Counted quantities will replace the book stock at the chosen date.",
      countedAt: "Inventory date",
      scope: "Scope",
      allProducts: "Whole catalog",
      created: "Inventory created",
      statuses: { draft: "In progress", validated: "Validated", cancelled: "Cancelled" },
      progress: "{counted, number} / {total, number} counted",
      book: "Book",
      counted: "Counted",
      difference: "Difference",
      save: "Save count",
      saved: "Count saved",
      validate: "Validate inventory",
      validateTitle: "Validate the inventory?",
      validateBody:
        "{count, plural, one {# difference will be booked} other {# differences will be booked}} on {date}. Uncounted items stay unchanged.",
      validated: "Inventory validated",
      cancel: "Cancel inventory",
      cancelled: "Inventory cancelled",
      emptyTitle: "No inventories",
      emptyBody: "Start an inventory to enter the opening stock or realign the book stock.",
      onlyDifferences: "Differences only",
      uncounted: "Not counted",
      lastValidated: "Last inventory validated on {date}",
      noneValidated: "No validated inventory",
    },
    valuation: {
      negativeNote:
        "{count, plural, one {# article is} other {# articles are}} in negative stock ({quantity} units): valued at 0 and left out of the totals, to fix with a count or an adjustment.",
      at: "Stock at",
      quantity: "Units in stock",
      purchaseValue: "Purchase value",
      saleValue: "Resale value excl. VAT",
      export: "Export CSV",
      filename: "stock-valuation-{date}.csv",
      emptyTitle: "No stock at this date",
    },
  },
  quotes: {
    title: "Quotes",
    singular: "Quote",
    new: "New quote",
    searchPlaceholder: "No., customer, reference…",
    emptyTitle: "No quotes",
    emptyBody: "Create a quote: it becomes an order as soon as it is accepted.",
    created: "Quote created",
    updated: "Quote saved",
    statuses: {
      draft: "Draft",
      sent: "Sent",
      accepted: "Accepted",
      declined: "Declined",
      expired: "Expired",
      cancelled: "Cancelled",
    },
    fields: {
      number: "No.",
      customer: "Customer",
      issueDate: "Date",
      validUntil: "Valid until",
      reference: "Customer reference",
      subject: "Subject",
      intro: "Introduction",
      introHint: "Empty: the template text (Settings › Documents) is printed.",
      deliveryTime: "Lead time",
      total: "Total incl. VAT",
      margin: "Margin",
    },
    invoiced: "Draft invoice created from the quote",
    actions: {
      invoice: "Invoice",
      accept: "Accept",
      decline: "Mark declined",
      cancel: "Cancel quote",
      duplicate: "Duplicate",
      send: "Send",
      pdf: "PDF preview",
      preview: "Preview",
      open: "Open the PDF",
      download: "Download",
    },
    previewTitle: "Quote preview",
    previewUnsaved: "Save the quote to open or download its PDF.",
    previewError: "The preview could not be generated: close it, the fields to correct are in red.",
    frozenPdf: "PDF frozen when sent: it no longer changes, even if the document template is edited.",
    acceptTitle: "Turn this quote into an order?",
    acceptBody: "The order reserves the items' stock and joins the orders to process.",
    acceptDeposit: "Ask for a deposit",
    acceptDepositHint: "The deposit invoice is created as a draft: you issue it once checked.",
    publicHint: "Administrative references (commitment, SIRET, purchase order) are entered on acceptance.",
    acceptPublicBody:
      "Enter the references of the purchase order received from the public body. They stay editable on the order until an invoice is issued.",
    accepted: "Order created",
    declined: "Quote marked declined",
    cancelled: "Quote cancelled",
    duplicated: "Quote duplicated",
    orderLink: "Order {number}",
    locked: "This quote can no longer be edited.",
  },
  orders: {
    board: {
      layout: "Layout",
      list: "List",
      board: "Preparation board",
      sort: "Sort the cards",
      sorts: { newest: "Newest first", oldest: "Oldest first", priority: "Preparation priority" },
      empty: "Nothing here.",
      items: "{count, plural, one {# item} other {# items}}",
    },
    title: "Orders",
    singular: "Order",
    new: "New order",
    searchPlaceholder: "No., customer, reference…",
    emptyTitle: "No orders",
    emptyBody: "Website orders, accepted quotes and manual entries land here.",
    allDone: "All caught up",
    allDoneBody: "No pending orders in this view.",
    views: {
      to_invoice: "To invoice",
      open: "To process",
      to_process: "New",
      preparing: "Preparing",
      shipped: "Shipped",
      delivered: "Delivered",
      archived: "Archived",
      cancelled: "Cancelled",
      all: "All",
    },
    statuses: {
      to_process: "To process",
      preparing: "Preparing",
      shipped: "Shipped",
      delivered: "Delivered",
      cancelled: "Cancelled",
    },
    sources: { shopify: "Website", quote: "Quote", manual: "Manual" },
    payment: {
      unpaid: "Unpaid",
      partially_paid: "Partially paid",
      paid: "Paid",
      refunded: "Refunded",
      partially_refunded: "Partially refunded",
    },
    flags: { urgent: "Urgent", ready: "Ready", stockout: "Out of stock", remainder: "Remainder" },
    fields: {
      allocated: "Set aside",
      orderedAt: "Date",
      customer: "Customer",
      items: "Items",
      total: "Total incl. VAT",
      source: "Source",
      payment: "Payment",
      reference: "Customer ref.",
      toShip: "To ship",
      shipped: "Shipped",
      invoiced: "Invoiced",
      available: "Avail.",
    },
    allocation: {
      pick: "Select",
      quantity: "Quantity",
      selected: "{count, plural, one {# line selected} other {# lines selected}}",
      isolate: "Set items aside",
      release: "Release",
      ship: "Ship selection",
      deliveryNote: "Delivery note of the selection",
      selectIsolated: "Select the items set aside",
      isolatedCount: "{count, plural, one {# line set aside} other {# lines set aside}}",
      done: "Items set aside for this order",
      released: "Items released",
      short: "Short in stock",
      left: "{count} left",
    },
    itemsCount: "{count, plural, one {# item} other {# items}}",
    actions: {
      deliveryNote: "Delivery note",
      handDelivery: "Delivered myself",
      markUrgent: "Mark urgent",
      unmarkUrgent: "Remove urgency",
      prepare: "Start preparing",
      delivered: "Mark delivered",
      cancel: "Cancel order",
      archive: "Archive",
      unarchive: "Unarchive",
      invoice: "Invoice",
      depositInvoice: "Deposit invoice",
      ship: "Ship",
    },
    created: "Order created",
    updated: "Order updated",
    invoiceCreated: "Draft invoice created",
    cancelTitle: "Cancel this order?",
    cancelBody: "The stock reserved by its items will be released.",
    sections: { lines: "Items", shipments: "Shipments", invoices: "Invoices", sav: "After-sales", activity: "History" },
    noShipments: "No shipments yet.",
    noInvoices: "Not invoiced yet.",
    invoicedInShopify: "Invoiced in Shopify (Simple Invoice) once paid: the invoice then shows here.",
    stockoutHint: "Available stock does not cover every line: restock or reprioritise.",
    fromQuote: "From quote",
    refs: {
      title: "Administrative references",
      engagementNumber: "Commitment number",
      billingSiret: "Billing SIRET",
      chorusServiceCode: "Chorus service code",
      purchaseOrderNumber: "Purchase order number",
      purchaseOrder: "Purchase order",
      signedDeliveryNote: "Signed delivery note",
      attachSignedDeliveryNote: "Attach the signed delivery note",
      attachmentsHint:
        "The purchase order and the signed delivery note travel inside the Factur-X invoice: Chorus Pro receives them as attachments of the deposit. 3.5 MB at most for both.",
      attach: "Attach the purchase order",
      replaceFile: "Replace the file",
      removeFile: "Remove the file",
      edit: "Edit references",
      defaultsHint: "Left empty, the SIRET and service code of the customer record apply to the invoice.",
      locked: "An invoice has been issued: these references are frozen.",
      missing: "Not received yet: enter them when the purchase order arrives.",
      saved: "References saved",
    },
  },
  invoices: {
    correctsLink: "Credit note on {number}",
    title: "Invoices",
    singular: "Invoice",
    new: "New invoice",
    searchPlaceholder: "No., customer…",
    emptyTitle: "No invoices",
    emptyBody: "Invoice an order or create a standalone invoice.",
    kinds: { invoice: "Invoice", credit_note: "Credit note", deposit: "Deposit invoice" },
    depositLine: "{share} deposit on {document}",
    deposit: {
      title: "Deposit invoice",
      create: "Ask for a deposit",
      hint: "A deposit invoice is created as a draft; the rest of the order will be invoiced net of this deposit.",
      share: "Share of the order (%)",
      amount: "or amount incl. VAT",
      ofTotal: "of {total} incl. VAT",
      preview: "Deposit: {amount} incl. VAT",
      readOnly: "Lines computed from the order: delete this invoice to ask for another amount.",
      deductedOn: "Taken off invoice {number}",
      deducted: "Deposits deducted",
      deductedLine: "{number} of {date}: {amount} excl. VAT",
      created: "Deposit invoice created",
    },
    takenOver:
      "Invoice taken over from IABAKO with its number and lines: the original stays in IABAKO, the ERP draws its PDF again. Payments and any credit note are handled here.",
    issuers: { erp: "ERP", shopify: "Shopify", iabako: "IABAKO" },
    external: {
      badge: "Shopify",
      banner:
        "Invoice issued in Shopify (Simple Invoice) under order number {number}: the original prevails. The ERP can reproduce its PDF.",
      creditNoteBanner:
        "Credit note issued in Shopify (Simple Invoice) for the refund of order {number}: the original prevails. The ERP can reproduce its PDF.",
      open: "Open the order in Shopify",
    },
    export: {
      action: "Export PDFs",
      hint: "A ZIP archive for the accountant: the PDF of every invoice and credit note issued in the period, with a CSV summary.",
      download: "Download archive",
      includeExternal: "Include Shopify invoices",
      includeExternalHint: "Reproduced by the ERP from the orders. The summary lists them in any case.",
      filename: "invoices-{from}-{to}.zip",
      downloaded: "Archive downloaded",
      columns: {
        number: "No.",
        kind: "Type",
        issuer: "Issuer",
        date: "Date",
        customer: "Customer",
        net: "Total excl. VAT",
        vat: "VAT",
        gross: "Total incl. VAT",
        paid: "Received",
      },
    },
    statuses: { draft: "Draft", issued: "Issued", cancelled: "Cancelled" },
    filters: {
      all: "All",
      unpaid: "Receivable",
      overdue: "Overdue",
      paid: "Settled",
      drafts: "Drafts",
      creditNotes: "Credit notes",
      shopify: "Shopify",
      iabako: "IABAKO",
    },
    summary: { receivable: "Receivable", overdue: "Of which overdue" },
    fields: {
      number: "No.",
      customer: "Customer",
      issueDate: "Issued",
      dueDate: "Due",
      total: "Total incl. VAT",
      paid: "Collected",
      balance: "Balance due",
      credited: "Credit notes",
    },
    chorus: {
      title: "Chorus Pro",
      resubmit: "Deposit again",
      queued: "Chorus Pro deposit scheduled",
      states: {
        none: "Not deposited",
        queued: "Queued",
        depositing: "Being filed",
        uncertain: "Filing to check",
        deposited: "Deposited",
        integrated: "Integrated",
        rejected: "Rejected",
        not_configured: "Not connected",
      },
    },
    inqom: {
      title: "Accountant (Inqom)",
      resubmit: "Deposit again",
      queued: "Inqom deposit scheduled",
      document: "Inqom document #{id}",
      states: {
        none: "Not deposited",
        queued: "Queued",
        depositing: "Being deposited",
        uncertain: "Deposit to check",
        deposited: "Deposited",
        failed: "Failed",
        not_configured: "Not connected",
      },
    },
    actions: {
      issue: "Issue",
      delete: "Delete draft",
      addPayment: "Record a payment",
      creditNote: "Cancel / Credit note",
      reinvoice: "Invoice the order again",
      unpay: "Set back to awaiting payment",
      send: "Send",
      pdf: "PDF preview",
      download: "Download",
    },
    bulk: {
      download: "{count, plural, one {Download # invoice} other {Download # invoices}}",
      clear: "Clear selection",
      filename: "invoices-selection-{date}.zip",
      downloaded: "Archive downloaded",
      tooMany: "At most {max} invoices per download.",
    },
    issueTitle: "Issue this document?",
    issueBody: "It receives a final number and can no longer be edited: any correction will require a credit note.",
    issued: "{number} issued",
    deleted: "Draft deleted",
    saved: "Draft saved",
    draftHint: "Unnumbered draft: you can still edit it.",
    overdue: "Overdue",
    accountingSent: "Sent to the accountant on {date}",
    related: "Related documents",
    paymentsTitle: "Payments",
    noPayments: "No payments recorded.",
    paymentAdded: "Payment recorded",
    paymentRemoved: "Payment removed",
    reinvoiced: "Order put back to invoice: new draft invoice",
    paymentsCleared: "Invoice set back to awaiting payment",
    payment: {
      amount: "Amount",
      paidAt: "Date",
      method: "Method",
      reference: "Reference",
      methods: { transfer: "Transfer", card: "Card", check: "Cheque", cash: "Cash", shopify: "Shopify", other: "Other" },
    },
    creditNote: {
      title: "Cancel the invoice or issue a credit note",
      whole: "Full credit note",
      partial: "Choose lines",
      reason: "Reason",
      created: "Draft credit note created",
      readOnly: "Lines taken from the corrected invoice: delete this draft to issue another credit note.",
    },
  },
  editor: {
    customer: "Customer",
    pickCustomer: "Search a customer…",
    lines: "Lines",
    searchProduct: "Add an item: name or SKU…",
    addFreeLine: "Free line",
    freeLineLabel: "Description",
    columns: {
      designation: "Description",
      quantity: "Qty",
      unitPrice: "Unit price",
      discount: "Discount %",
      vat: "VAT",
      total: "Total excl. VAT",
    },
    removeLine: "Remove line",
    detailKit: "Detail into articles (separate sizes)",
    moveUp: "Move up",
    moveDown: "Move down",
    description: "Description",
    addDescription: "Add a description",
    globalDiscount: "Overall discount %",
    shipping: "Shipping excl. VAT",
    notes: "Notes shown on the document",
    terms: "Specific terms",
    totals: {
      deposit: "Deposit already invoiced ({number})",
      subtotal: "Subtotal excl. VAT",
      globalDiscount: "Overall discount",
      shipping: "Shipping",
      net: "Total excl. VAT",
      vat: "VAT {rate}",
      gross: "Total incl. VAT",
      cost: "Cost",
      uncosted: "Margin computed without {amount} of articles with no purchase cost.",
      margin: "Margin",
    },
    lowMargin: "Margin below the {rate} threshold",
    noCost: "No purchase cost",
    availability: "{count, number} avail.",
    noLines: "Add an item or a free line.",
    unsaved: "Unsaved changes",
  },
  email: {
    title: "Send by email",
    to: "To",
    cc: "Cc",
    subject: "Subject",
    message: "Message",
    attachment: "The PDF is attached automatically.",
    send: "Send",
    queued: "Email being sent",
    history: "Sent emails",
    statuses: { queued: "Queued", sent: "Sent", failed: "Failed" },
    notConfigured: "Email sending is not configured yet: download the PDF and send it from your mailbox.",
  },
  company: {
    logo: "Document logo",
    logoHint: "PNG or JPEG, 2 MB maximum. Printed at the top of quotes, invoices, delivery notes and purchase orders.",
    logoDefault: "Phygiatech logo",
    logoUpload: "Change logo",
    logoRemove: "Back to the Phygiatech logo",
    title: "Company and documents",
    identity: "Legal identity",
    legalName: "Legal name",
    tradeName: "Trade name",
    legalForm: "Legal form",
    capital: "Share capital",
    siren: "SIREN",
    rcs: "Registration",
    vatNumber: "VAT number",
    deliveryAddresses: {
      title: "Purchase delivery addresses",
      hint: "Where your suppliers deliver (warehouse, an associate's address…). The address above stays the billing one. A purchase order keeps the address chosen, even if you edit it here later.",
      name: "Address name",
      nameHint: 'For instance "Storage24 warehouse": this is what you pick on the order.',
      contact: "Contact (first and last name)",
      default: "Default",
      add: "Add an address",
      none: "None: purchase orders are delivered to the company's address.",
      company: "Company address",
      removed: "{name} (removed from the settings)",
    },
    eori: "EORI number",
    eoriHint:
      "Customs number (imports and exports outside the EU), printed on purchase orders and sent to Colissimo for parcels leaving the EU.",
    customs: "Customs",
    customsHint:
      "Defaults of the CN23 declaration of a parcel leaving the European Union or going overseas, for articles that have none on their card.",
    customsOriginCountry: "Default country of origin (2 letters)",
    customsHsCode: "Default HS code",
    contact: "Contact details",
    bank: "Bank details",
    bankHint: "Shown on invoices for bank transfers.",
    iban: "IBAN",
    bic: "BIC",
    documents: "Documents",
    quoteValidityDays: "Quote validity (days)",
    defaultPaymentTermsDays: "Default payment terms (days)",
    marginAlert: "Margin alert below (%)",
    archiveAfterDays: "Archive delivered or cancelled orders after (days)",
    savAlertDays: "Flag an after-sales case open for (days)",
    defaults: "Lead times and thresholds",
    accounting: "Accounting",
    accountingEmail: "Accountant's collection address (Inqom)",
    accountingHint: "Every issued invoice and credit note is sent there automatically.",
    webInvoiceMode: "Website order invoices",
    creditNoteEngagement: "Repeat the commitment number on credit notes",
    autoReplenish: "Automatic restocking",
    depositInvoices: "Deposit invoices",
    depositDefaultBp: "Deposit proposed by default (%)",
    serialTracking: "Serial number tracking",
    serialTypes: "Tracked articles, by product type",
    serialTypeRow: "{tracked} / {products} products tracked — {onHand} units in stock",
    serialTypeBlocked: "{product}: {message}",
    serialEnableType: "Track the whole type",
    serialDisableType: "Stop tracking the type",
    parcelWeights: "Average parcel weights",
    parcelWeightsHint:
      "Weight counted for an item with no weight of its own, by product type: used to estimate after-sales return labels, replacements and exchanges.",
    parcelWeightOf: "{type} (g)",
    noFamily: "No family",
    parcelWeightDefault: "Other items (g)",
    parcelWeightTypeHint: "Empty: the weight of other items applies.",
    parcelFormats: "La Poste Pro Expédition parcels",
    parcelFormatsHint: "Dimensions and content category of the shipments file for an order whose box has not been chosen yet.",
    parcelFormatDefault: "Default format",
    parcelDimensions: "L × W × H (cm)",
    parcelCategory: "La Poste content category",
    saved: "Settings saved",
  },
  sav: {
    title: "After-sales",
    singular: "After-sales case",
    new: "New case",
    searchPlaceholder: "Search a case, customer, subject…",
    statuses: {
      open: "Open",
      awaiting_return: "Awaiting return",
      received: "Return received",
      resolved: "Resolved",
      closed: "Closed",
    },
    views: {
      active: "In progress",
      open: "Open",
      awaiting_return: "Awaiting return",
      received: "Received",
      resolved: "Resolved",
      closed: "Closed",
    },
    reasons: {
      defect: "Product defect",
      damaged: "Damaged in transit",
      size_exchange: "Size exchange",
      product_exchange: "Product exchange",
      wrong_item: "Wrong item",
      withdrawal: "Withdrawal",
      cancellation: "Cancellation",
      other: "Other",
    },
    resolutions: { refund: "Refund", replacement: "Replacement", repair: "Repair", none: "No action" },
    conditions: { restock: "Restocked", quarantine: "Quarantine", scrap: "Scrapped" },
    kinds: { sav: "After-sales", exchange: "Exchange", return: "Return" },
    kindHints: {
      sav: "Product problem: diagnosis, possible return, then replacement, repair or refund.",
      exchange: "Another size or model: the new item leaves with a return label in the parcel.",
      return: "Withdrawal or cancellation: the customer sends the item back, then is refunded.",
    },
    allKinds: "All types",
    fields: {
      returnTo: "Return addressee",
      returnFrom: "Sender (the customer)",
      returnToName: "Name (post office, warehouse…)",
      emailLabel: "E-mail the label to the customer ({email})",
      contactName: "Customer name",
      article: "Article concerned",
      photos: "Photos or documents",
      kind: "Request type",
      number: "No.",
      customer: "Customer",
      subject: "Subject",
      reason: "Reason",
      resolution: "Resolution",
      description: "Description",
      order: "Order",
      age: "Opened",
      lines: "Items concerned",
      quantity: "Qty",
      received: "Received",
      condition: "Condition",
      label: "Description",
      variant: "Catalog item",
      tracking: "Tracking number",
      generateLabel: "Generate the Colissimo label",
      creditNote: "Credit note",
      quote: "Quote",
      sentVariant: "Item sent",
      returnTracking: "Return tracking number",
    },
    sections: {
      lines: "Items",
      timeline: "Case timeline",
      shipments: "Related shipments",
      customer: "Customer",
      links: "Related documents",
    },
    events: {
      undo: "Action undone",
      email: "Return label e-mailed to {to}",
      note: "Note",
      status: "Status: {status}",
      statusResolution: "Resolved: {resolution}",
      returnLabel: "Return label {number}",
      laposteReturn: "La Poste return file generated",
      replacement: "Replacement shipped {number}",
      receipt: "Return received ({count, plural, one {# item} other {# items}})",
      refundShopify: "Refund to be made in Shopify",
      refundShopifyApi: "Refund started in Shopify",
      refundCredit: "Draft credit note created",
    },
    actions: {
      undo: "Undo the last action",
      create: "Open the case",
      openFromOrder: "Open an after-sales case",
      returnLabel: "Return label only",
      laposteReturn: "La Poste file (return)",
      receive: "Receive the return",
      replace: "Send a product",
      refund: "Refund",
      resolveRepair: "Resolve as repaired",
      resolveNone: "Resolve with no action",
      close: "Close",
      reopen: "Reopen",
      addNote: "Add note",
      addLine: "Add an item",
      openLabel: "Open the label",
      viewCreditNote: "View credit note",
      attach: "Attach a file",
      exchange: "Product + return label",
      changeOrder: "Change order",
      withoutOrder: "Case without an order",
      withOrder: "Link to an order",
    },
    hints: {
      contactName:
        "Grouped order: the name of the person concerned, when not the order's customer. It shows on the case and its labels.",
      photos: "The customer's photos (damage, label…): attached to the case as soon as it opens.",
      fromOrder: "Tick the items concerned by the request.",
      orderFirst: "Pick the order: its items will show here.",
      noOrder: "No linked order: choose the customer and describe the items.",
      condition: "Only items marked “Restocked” go back to sellable stock. Quarantine and scrap stay recorded on the case.",
      returnLabel:
        "Creates the return label (PDF to pass on to the customer). Nothing leaves stock; the case waits for the parcel, checked on receipt.",
      laposteReturn:
        "File to import in La Poste Pro Expédition to print the return label (parcel addressed to the company, weight and format from the items). The case then waits for the parcel.",
      laposteReturnDone: "Return file downloaded",
      labelUnavailable: "Colissimo is not configured: enter the tracking number manually.",
      replacement:
        "Sends the replacement items right away (they leave stock, delivery note created), without waiting for a return; the case is resolved.",
      refundShopify:
        "This order comes from Shopify: the ERP refunds the customer in Shopify when writing to Shopify is enabled, otherwise do it in the Shopify admin. The credit note is created when the refund arrives.",
      refundCredit: "A draft credit note will be prepared on the order's invoice, to review then issue.",
      notePlaceholder: "Exchange with the customer, findings, decision…",
      stale: "Open for more than 14 days",
      noLines: "No items on this case.",
      noShipments: "No shipments yet.",
      pickOrder: "Find the order by its number or the customer's name to tick the items concerned.",
      exchange:
        "The new item leaves stock and ships with the return label in the parcel. The case then waits for the returned item, checked on receipt.",
      sameVariant: "Pick the item to send if it differs (other size, other model).",
    },
    refundTitle: "Refund this case?",
    closeTitle: "Close this case?",
    closeBody: "The case becomes read-only. You can reopen it if needed.",
    empty: "No after-sales case in progress",
    emptyBody: "Customer requests will appear here as soon as they are opened.",
    created: "After-sales case opened",
    updated: "Case updated",
    noteAdded: "Note added",
    refundedCredit: "Draft credit note created",
    refundedShopify: "Refund recorded; complete it in Shopify",
    refundedShopifyApi: "Refund started in Shopify; the credit note will follow",
    attachmentAdded: "File attached",
    pickCustomer: "Choose a customer",
    wholeSet: "Whole set",
    returnEmail: {
      subject: "Your return label — {company} (case {number})",
      body: "Hello {name},\n\nPlease find attached the return label for your case {number}. Print it, stick it on the parcel and drop it at the post office.\n\nKind regards,\n{company}",
    },
    returnParcel: "Return parcel",
    sentParcel: "Parcel sent",
    returnToCompany: "The company address (no return address set)",
    undoTitle: "Undo the last action?",
    undoBody:
      "The case goes back to how it was: parcels it created are cancelled (what left comes back into stock; a label already printed is simply not used), a receipt is taken back out of stock. A refund is not undone here.",
    pickKind: "Type of request",
    pickKindFirst: "Choose the type of request first",
    pickReason: "Choose a reason",
    pickVariant: "Search an item (optional)",
    pickOrder: "Order number or customer name",
    pickSentVariant: "Same item",
  },
  parcels: {
    labelFormat: "Colissimo label format",
    labelFormatHint:
      "10×15 for a thermal label printer, A4 for an ordinary printer (the 10×15 label sits on the page). An international return label is always A4.",
    labelFormats: { PDF_10x15_300dpi: "10×15 cm (label printer)", PDF_A4_300dpi: "A4 (ordinary printer)" },
    chronopostLabelFormat: "Chronopost label format",
    chronopostLabelFormatHint:
      "An A4 page with or without the proof of deposit for an ordinary printer, or a 10×15 label for a thermal label printer.",
    chronopostLabelFormats: {
      PDF: "A4 with proof of deposit (ordinary printer)",
      SPD: "A4 without proof of deposit (ordinary printer)",
      THE: "10×15 cm (label printer)",
    },
    printLabel: "Print the label",
    parcel: "Parcel {n}",
    summary: "Parcel {n} — {length}×{width}×{height} cm",
    summaryEnvelope: "Parcel {n} — envelope {length}×{width} cm",
    envelope: "Envelope (flat, no height)",
    freeEnvelope: "Free envelope (enter dimensions)",
    envelopeBadge: "Envelope",
    weight: "{grams} g",
    box: "Box",
    free: "Free size (enter the dimensions)",
    length: "Length (cm)",
    width: "Width (cm)",
    height: "Height (cm)",
    add: "Add a parcel",
    weightHint: "Proposed: {grams} g (articles + empty box).",
    boxes: "Boxes",
    boxesHint:
      'The boxes you keep in stock: picked at shipment, their dimensions go to the La Poste file with the weight. "Free size" is always possible.',
    boxName: "Name",
    emptyGrams: "Empty weight (g)",
    addBox: "Add a box",
    laposteFile: "La Poste file",
    count: "{count, plural, one {# parcel} other {# parcels}}",
  },
  shipments: {
    title: "Shipments",
    singular: "Shipment",
    newTitle: "Prepare a shipment",
    newSubtitle: "Order {number}",
    searchPlaceholder: "Parcel no., order, customer, tracking",
    filters: { allStatuses: "All statuses", allCarriers: "All carriers", from: "From", to: "To" },
    statuses: { draft: "Draft", ready: "Ready", shipped: "Shipped", delivered: "Delivered", cancelled: "Cancelled" },
    kinds: {
      outbound: "Customer shipment",
      return: "After-sales return",
      replacement: "After-sales replacement",
      free: "Without order",
      resend: "Resend",
    },
    views: { all: "All" },
    carriers: { colissimo: "Colissimo", chronopost: "Chronopost", other: "Other carrier" },
    handDelivery: "Handed over in person",
    services: { DOM: "Colissimo home, no signature", DOS: "Colissimo home, with signature", COLD: "Colissimo Expert" },
    fields: {
      number: "Delivery note",
      recipient: "Recipient",
      order: "Order",
      date: "Date",
      tracking: "Tracking number",
      carrier: "Carrier",
      service: "Service",
      weight: "Weight (g)",
      remaining: "Remaining",
      onHand: "Stock",
      toShip: "To ship",
      shippedAt: "Shipped on",
      deliveredAt: "Delivered on",
      address: "Delivery address",
      sav: "After-sales case",
      note: "Reason",
    },
    sections: { lines: "Parcel contents", parcel: "Parcel", recipient: "Recipient", tracking: "Tracking", activity: "History" },
    actions: {
      ship: "Confirm shipment",
      deliveryNote: "Delivery note",
      label: "Label",
      setTracking: "Add tracking",
      editTracking: "Edit tracking",
      delivered: "Mark delivered",
      track: "Track parcel",
      openOrder: "View order",
      openSav: "View after-sales case",
      shipAll: "Ship everything",
      shipNone: "Clear all",
      resend: "Send again",
    },
    hints: {
      noColissimo: "No carrier is connected (Colissimo, Chronopost): enter the tracking number of a label made elsewhere.",
      generateLabel: "Generate the Colissimo label",
      generateLabelChronopost: "Generate the Chronopost label",
      generateLabelHint: "The label is created on confirmation; if the carrier rejects it, nothing is saved.",
      trackingOptional: "Optional, can be added later.",
      weight: "Pre-filled from the item weights.",
      stockShort:
        "Not enough stock for {count, plural, one {# item} other {# items}}. You can still ship, but stock will go negative.",
      nothingToShip: "Everything on this order has already shipped.",
      noTracking: "No tracking number yet.",
      stockLeaves: "Stock leaves on confirmation; the order and Shopify are updated.",
    },
    toasts: { shipped: "Shipment {number} confirmed", tracking: "Tracking saved", delivered: "Shipment marked delivered" },
    cancel: {
      action: "Cancel shipment",
      title: "Cancel shipment {number}?",
      stock: "The parcel's items go back into stock, at the cost they left with.",
      serials: "The parcel's serial numbers go back into stock.",
      order: "Order {number} goes back to preparation: this parcel's quantities are to ship again.",
      label: "The label is not cancelled at the carrier: do it from the carrier's account if it was paid for.",
      done: "Shipment {number} cancelled",
    },
    empty: { title: "No shipments", body: "Shipments are prepared from an order to process." },
    trackingTitle: "Tracking number",
    free: {
      action: "Ship without order",
      title: "Shipment without order",
      subtitle: "Sample, loan, trade show, internal use: shipment without an order",
      linesHint: "Items leaving stock.",
      noteHint: "Printed on the delivery note and in the stock ledger.",
      stockLeaves: "Stock leaves on confirmation, with delivery note and label.",
    },
    laposte: {
      action: "La Poste Pro Expédition file",
      actionCount: "La Poste file ({count, plural, one {# order} other {# orders}})",
      hint: "To import in La Poste Pro Expédition (import shipments from a file): labels are made there; the tracking number comes back through Shopify or is typed on the shipment.",
      filename: "laposte-pro-expedition-{date}.csv",
      downloaded: "La Poste file downloaded",
      nothing: "No order to ship in this list.",
    },
    resend: {
      title: "Send this parcel again",
      hint: "Same contents and address as {number}. The items leave stock again.",
      notePlaceholder: "Parcel lost, arrived damaged…",
    },
  },
  purchasing: {
    close: "Close the reception",
    closeTitle: "Close the reception?",
    closeBody:
      "What arrived is what the order delivered: quantities not received are no longer expected in stock nor owed, with no credit note to ask for. The order turns « Invoice to file » until the supplier's invoice is filed. Cannot be undone.",
    closeReason: "Reason (optional)",
    closed: "Reception closed on {date}",
    invoicePendingHint:
      "Invoice to file: drop the supplier's invoice in “Attachments”. It goes to the accountant and the order is archived.",
    title: "Purchasing",
    allSuppliers: "All suppliers",
    singular: "Purchase order",
    new: "New purchase order",
    searchPlaceholder: "No., supplier, reference…",
    views: { orders: "Orders", suggestions: "To reorder", payables: "To pay", documents: "Documents" },
    documents: {
      title: "Purchase documents",
      hint: "Supplier invoices, till receipts and contracts, kept here and handed to the accountant (Inqom API or collection address).",
      drop: "Drop your files here or",
      choose: "choose them",
      accepted: "PDF, JPEG or PNG, at most 10 MB per file.",
      uploaded: "{count, plural, one {# document added} other {# documents added}}",
      creditHint:
        "Optional: the credit note's amount, in the order currency and on the same basis as its total (no VAT), is deducted from what is still owed on the order. Not needed for goods never delivered on a closed reception.",
      creditNoOrder: "Credit note not linked to an order: it is not deducted from any balance.",
      uploadSheet: {
        title: "{count, plural, one {Add # document} other {Add # documents}}",
        description: "Say what the document is before it goes to the accountant: it is handed over as soon as it is added.",
        batchHint: "The type and date apply to every file.",
        creditOneByOne: "Add credit notes one at a time: each has its own amount.",
        invoiceAfterClose:
          "The supplier's invoice is added once the reception is closed: it goes to the accountant as soon as it is added and must reflect what was delivered.",
        submit: "Add",
      },
      kinds: {
        supplier_invoice: "Supplier invoice",
        supplier_credit: "Supplier credit note",
        receipt: "Till receipt",
        contract: "Contract",
        other: "Other",
      },
      fields: {
        name: "File",
        kind: "Type",
        date: "Document date",
        amount: "Amount",
        supplier: "Supplier",
        purchaseOrder: "Purchase order",
        note: "Note",
        transmission: "Accountant",
      },
      filters: { all: "All", pending: "To transmit", transmitted: "Transmitted" },
      transmission: {
        none: "Not transmitted",
        queued: "Queued",
        depositing: "Being deposited",
        uncertain: "Deposit to check",
        deposited: "Deposited at Inqom",
        failed: "Failed",
        not_configured: "Not connected",
        email: "Sent by e-mail",
      },
      actions: {
        open: "Open",
        edit: "Edit",
        transmit: "Transmit",
        transmitPending: "Transmit pending documents",
        attach: "Attach a document",
        delete: "Delete",
      },
      edited: "Document saved",
      deleted: "Document deleted",
      transmitted: "Transmission scheduled",
      pendingQueued: "{count, plural, =0 {No pending document} one {# document scheduled} other {# documents scheduled}}",
      deleteTitle: "Delete this document?",
      deleteBody: "The file is removed from the ERP. A document already transmitted to the accountant cannot be deleted.",
      emptyTitle: "No document",
      emptyBody: "Drop a supplier invoice, a receipt or a contract.",
      onOrder: "Attached documents",
      noneOnOrder: "No document attached to this order.",
    },
    payables: {
      due: "Outstanding",
      all: "Full history",
      total: "Still owed to suppliers:",
      toRecover: "to recover (overpaid, cancelled order):",
      expected: "Expected on {date}",
      lastPaid: "last paid on {date}",
      credited: "credit notes {amount}",
      paidOf: "{paid} paid of {total}",
      emptyTitle: "Nothing to pay",
      emptyBody: "Every sent purchase order is fully paid.",
    },
    statusFilter: { open: "Open", auto: "Auto restock", archived: "Archived", all: "All" },
    emptyTitle: "No purchase orders",
    emptyBody: "Create an order or start from the reorder suggestions.",
    created: "Purchase order created",
    updated: "Purchase order saved",
    statusUpdated: "Status updated",
    statuses: {
      draft: "Draft",
      sent: "Sent",
      confirmed: "Confirmed",
      partially_received: "Partly received",
      received: "Fully received",
      invoice_pending: "Invoice to file",
      closed: "Closed",
      cancelled: "Cancelled",
    },
    fields: {
      number: "No.",
      supplier: "Supplier",
      currency: "Currency",
      exchangeRate: "Exchange rate",
      exchangeRateHint: "Value of 1 {currency} in euros. Received goods enter stock at this rate.",
      orderedAt: "Order date",
      expectedAt: "Requested delivery",
      deliveryAddress: "Deliver to",
      deliveryAddressHint: "Delivery addresses are kept in Settings › Identity; printed on the purchase order.",
      supplierReference: "Supplier reference",
      notes: "Notes for the supplier",
      progress: "Received",
      progressValue: "{received} / {ordered}",
      total: "Total excl. VAT",
      paid: "Paid",
      credited: "Supplier credit notes",
      remainingToPay: "Left to pay",
      invoiced: "Invoiced by the supplier",
      receivedValue: "Value received",
      item: "Item",
      supplierSku: "Supplier ref.",
      quantity: "Qty",
      unitCost: "Unit cost",
      lineTotal: "Total",
      received: "Received",
      remaining: "Remaining",
    },
    editor: {
      addLine: "Add an item",
      addLinePlaceholder: "Search a SKU or product…",
      supplierPlaceholder: "Search a supplier…",
      noLines: "Add the items to order.",
      removeLine: "Remove line",
      totalInCurrency: "Total in {currency}",
      totalInEuros: "About {amount}",
    },
    editHint: "Editable until the reception is closed",
    resendOffer: "Order changed after sending: send the new version to the supplier.",
    actions: {
      edit: "Edit",
      pdf: "PDF preview",
      resend: "Send again",
      send: "Send to supplier",
      markSent: "Mark as sent",
      confirm: "Mark confirmed",
      cancel: "Cancel order",
      receive: "Receive",
      addPayment: "Add a payment",
      deletePayment: "Delete payment",
      createOrder: "Create order",
    },
    cancelTitle: "Cancel this purchase order?",
    cancelBody: "Its quantities will no longer count as incoming stock.",
    overdue: "Overdue",
    sections: {
      lines: "Items",
      supplier: "Supplier",
      payments: "Payments",
      receipts: "Receipts",
      activity: "History",
    },
    noPayments: "No payments recorded.",
    noReceipts: "Nothing received yet.",
    receiptLine: "{count, plural, one {# item} other {# items}} — {value}",
    receipt: {
      title: "Receipt",
      description: "Received quantities enter stock and update the average cost.",
      date: "Receipt date",
      all: "Receive everything",
      none: "Reset to zero",
      submit: "Confirm receipt",
      done: "Receipt recorded",
      nothing: "Enter at least one received quantity.",
    },
    payment: {
      title: "Supplier payment",
      kind: "Type",
      amount: "Amount",
      date: "Payment date",
      reference: "Reference",
      bankAccount: "Bank",
      bankAccountHint: "Add your accounts in Settings › Purchasing to pick them from a list.",
      added: "Payment recorded",
      deleted: "Payment deleted",
      kinds: { deposit: "Deposit", balance: "Balance", transport: "Transport", other: "Other" },
    },
    replenish: {
      badge: "Auto restock",
      refresh: "Update now",
      updated: "{lines} line(s) updated on {orders} draft(s)",
      upToDate: "The restock drafts are up to date",
      bannerTitle: "Automatic restock draft",
      bannerBody:
        "Products that fall below their alert threshold are added here on their own, every hour, until the order is sent. Lines you change are no longer touched and a removed product does not come back. Nothing is sent to the supplier without you.",
      rateHint: "Foreign-currency order: check the exchange rate before sending (taken from the last order).",
      intro:
        "One draft purchase order per supplier fills itself with the products below their alert threshold, to group orders and shipping.",
      seeDrafts: "See the restock drafts",
    },
    suggestions: {
      intro:
        "Items at or below their threshold, counting orders already placed. The suggested quantity refills to the target stock.",
      emptyTitle: "Nothing to reorder",
      emptyBody: "Every tracked item is above its alert threshold.",
      noSupplier: "No main supplier",
      noSupplierHint: "Set the main supplier of these items to be able to order them.",
      leadTime: "Lead time {days, plural, one {# day} other {# days}}",
      available: "Available",
      incoming: "Incoming",
      reorderPoint: "Threshold",
      target: "Target",
      suggested: "To order",
      lastCost: "Average cost",
      openProduct: "Open product",
      priceHint: "Check purchase prices in the supplier's currency before sending.",
      rateLabel: "Rate (1 {currency} in €)",
    },
  },
  documents: {
    simulatedLabel: {
      title: "TEST LABEL",
      notice: "Simulation: no label was generated at La Poste. Do not stick on a parcel.",
      reference: "Reference: {reference}",
      weight: "Weight: {weight} g",
    },
    eori: "EORI {number}",
    quote: "Quote",
    invoice: "Invoice",
    creditNote: "Credit note",
    deliveryNote: "Delivery note",
    purchaseOrder: "Purchase order",
    draft: "Draft",
    number: "No. {number}",
    issueDate: "Date: {date}",
    validUntil: "Valid until {date}",
    dueDate: "Due date: {date}",
    orderRef: "Order {number}",
    reference: "Your reference: {reference}",
    customerOrder: "Your order no. {number}",
    engagement: "Commitment no.: {number}",
    corrects: "Credit note for invoice {number}",
    deposit: "Deposit invoice",
    depositMeta: "{share} deposit on {document}",
    depositVatNote: "Deposit invoice: VAT is due on receipt of payment.",
    vatExemption: {
      intraCommunity: "VAT exempt, article 262 ter, I of the French tax code: intra-community supply.",
      export: "VAT exempt, article 262 I of the French tax code: export outside the European Union.",
      exempt: "VAT-exempt transaction.",
    },
    finalAfterDeposit: "Final invoice after deposit",
    depositDeducted: "Deposit already invoiced — {number} of {date}",
    billTo: "Bill to",
    shipTo: "Ship to",
    returnFrom: "Returned by",
    supplier: "Supplier",
    sku: "Ref.",
    designation: "Description",
    quantity: "Qty",
    unitPrice: "Unit price excl. VAT",
    discount: "Discount",
    vat: "VAT",
    amount: "Amount excl. VAT",
    subtotal: "Subtotal excl. VAT",
    globalDiscount: "Overall discount",
    shipping: "Shipping",
    totalNet: "Total excl. VAT",
    vatLine: "VAT {rate} on {base}",
    totalGross: "Total incl. VAT",
    paid: "Already paid",
    balanceDue: "Balance due",
    paymentTerms: "Payment {days, plural, =0 {on receipt} one {within # day} other {within # days}}, no later than {date}.",
    noEarlyDiscount: "No discount for early payment.",
    latePenaltyPro:
      "Late payment: interest at three times the French legal rate and a €40 fixed recovery fee (French Commercial Code, art. L441-10).",
    latePenaltyConsumer: "Late payment may incur interest at three times the French legal rate.",
    bank: "Bank transfer: IBAN {iban} — BIC {bic}",
    acceptance: "Approved — date, signature and stamp:",
    goodsNature: "Supply of goods",
    legal: "{legalName} ({tradeName}) — {legalForm} with a share capital of {capital} — {rcs} — VAT {vat}",
    page: "Page {page} / {pages}",
    siret: "SIRET {siret}",
    vatNumber: "VAT {vat}",
    chorus: "Service {code}",
    thanks: "Thank you for your business.",
    siren: "SIREN {siren}",
    subject: "Subject",
    deliveryTime: "Lead time: {value}",
    watermark: { draft: "Draft", preview: "Preview" },
    sample: {
      customer: "Example Company Ltd",
      address: "12 Peace Street",
      reference: "Tender 2026-07",
      subject: "Equipment for 12 security officers",
      deliveryTime: "3 weeks after acceptance",
      supplier: "Sample Supplier GmbH",
      line1: "NIJ IV ballistic plate — 25 × 30 cm",
      line1Description: "Ceramic and polyethylene, 2.6 kg, NIJ 0101.06 certified.",
      line2: "Tactical plate carrier — size L",
      line3: "Customisation: logo marking",
      notes: "Offer valid for the stated quantity. Fitting training included.",
    },
    signature: "Received in good condition — date and signature:",
    expectedAt: "Requested delivery: {date}",
    email: {
      purchaseDocumentSubject: "{kind} — {name} — {company}",
      quoteSubject: "Quote {number} — {company}",
      quoteBody:
        "Hello,\n\nPlease find attached our quote {number}, valid until {date}.\n\nWe remain at your disposal for any question.\n\nBest regards,\n{company}",
      invoiceSubject: "Invoice {number} — {company}",
      invoiceBody: "Hello,\n\nPlease find attached invoice {number} for {amount}, due by {date}.\n\nBest regards,\n{company}",
      creditNoteSubject: "Credit note {number} — {company}",
      creditNoteBody: "Hello,\n\nPlease find attached credit note {number} for {amount}.\n\nBest regards,\n{company}",
      purchaseOrderSubject: "Purchase order {number} — {company}",
      purchaseOrderBody:
        "Hello,\n\nPlease find attached our purchase order {number}. Kindly confirm quantities and delivery lead time.\n\nBest regards,\n{company}",
      accountingSubject: "{kind} {number} — {company}",
    },
  },
  imports: {
    iabakoSync: {
      title: "Sync IABAKO",
      body: "Reads the quotes and sales created or changed in IABAKO and shows what will be taken over. Nothing is written before « Apply ». IABAKO is never changed.",
      read: "Read IABAKO",
      reading: "Reading IABAKO. This takes a few minutes; you can leave the page.",
      readAt: "Read on {date}.",
      appliedAt: "Applied on {date}.",
      unchanged:
        "{count, plural, =0 {} one {# document already identical in the ERP.} other {# documents already identical in the ERP.}}",
      expired: "The last read was replaced or is no longer valid. Read again.",
      upToDate: "The ERP is up to date: nothing to take over.",
      toApply: "To take over",
      actions: { create: "New", update: "Updated", unchanged: "Unchanged" },
      newCustomers: "Customers that will be created",
      newCustomersHint:
        "If one of them already exists in the ERP under another name, pick it: the document will be linked to it.",
      linkCustomer: "Link to an existing customer…",
      linkCustomerFor: "ERP customer for {name}",
      unresolved: "To resolve (not taken over)",
      reasons: {
        unknownArticle: "IABAKO article « {detail} » unknown to the ERP.",
        ambiguousArticle: "IABAKO article « {detail} »: several possible ERP articles.",
        unreadable: "Unreadable document ({detail}): take it over by hand.",
        unknownStatus: "Unrecognised IABAKO status: check by hand.",
        missingSourceQuote: "The source quote {detail} is not in the ERP.",
        creditNote: "Credit note or other document type ({detail}): reconcile by hand.",
        payments: "Several payments in IABAKO: enter them by hand.",
        totalGap: "The recomputed total differs from IABAKO (in cents: IABAKO/ERP {detail}).",
      },
      linkArticle: "Link to an ERP article…",
      linkArticleFor: "ERP article for « {label} »",
      noArticle: "Line without article",
      gaps: "Totals differ from IABAKO: {numbers}. Applying is blocked.",
      stale: "Preview older than 30 minutes: read again.",
      apply: "{count, plural, one {Apply (# document)} other {Apply (# documents)}}",
      applied: "IABAKO documents taken over in the ERP",
    },
    shopifySync: {
      title: "Shopify sync",
      body: "Fetches Shopify orders and stock right away, without waiting for the automatic sync (every 15 minutes).",
    },
    title: "Import data",
    subtitle: "CSV or Excel exported from IABAKO or a spreadsheet.",
    target: "Data type",
    targets: {
      customers: "Customers",
      suppliers: "Suppliers",
      products: "Products",
      documents: "Quotes and invoices (archive)",
    },
    dropzone: "Drop a file here or click to browse",
    mapping: "Column mapping",
    mappingHint: "Match each file column with an ERP field.",
    ignore: "Ignore this column",
    preview: "Preview",
    steps: {
      file: "File",
      mapping: "Columns",
      preview: "Check",
      report: "Result",
    },
    csvOnly: "Accepted formats: Excel (.xlsx, first sheet) or CSV (comma or semicolon separated).",
    fileError: "This file could not be read. Save it as .xlsx or CSV UTF-8 and try again.",
    emptyFile: "This file has no data rows.",
    fileSummary: "{name} — {count, plural, one {# row} other {# rows}}, {columns, plural, one {# column} other {# columns}}",
    changeFile: "Change file",
    column: "File column",
    field: "ERP field",
    sample: "Example",
    missingRequired: "Map the required fields: {fields}",
    duplicates: "Rows already in the ERP",
    duplicateModes: {
      skip: "Skip them",
      update: "Update existing records",
    },
    duplicateHints: {
      customers: "A customer is matched by email, otherwise by SIRET.",
      suppliers: "A supplier is matched by name.",
      products:
        "A product is matched by SKU; only prices, cost, EAN, weight, thresholds and supplier are updated. Opening stock is only booked for an item with no history. “New reference” renames the SKU; “Composition” (REF-A + 2 x REF-B) replaces the components.",
      documents:
        "A document is matched by type and number. Archives stay read-only: they never enter numbering, stock or accounting. The customer is found by email, otherwise by name.",
    },
    check: "Check",
    checking: "Checking…",
    summary: {
      created: "{count, plural, =0 {Nothing created} one {# created} other {# created}}",
      updated: "{count, plural, =0 {Nothing updated} one {# updated} other {# updated}}",
      skipped: "{count, plural, =0 {No duplicates skipped} one {# duplicate skipped} other {# duplicates skipped}}",
      renamed: "{count, plural, one {# reference renamed} other {# references renamed}}",
      compositions: "{count, plural, one {# composition set} other {# compositions set}}",
      errors: "{count, plural, =0 {No errors} one {# row with errors} other {# rows with errors}}",
    },
    status: {
      new: "New",
      duplicate: "Duplicate",
      invalid: "Error",
    },
    line: "Row {line}",
    previewHint: "Rows with errors will not be imported. Fix the file and load it again to include them.",
    previewMore: "… and {count, plural, one {# more row} other {# more rows}}",
    imported: "Import complete",
    restart: "Import another file",
    kindValues:
      "Detected type: “business”, “company” → business; “public”, “administration”, “police” → public body; otherwise individual.",
    fields: {
      kind: "Customer type",
      companyName: "Company",
      firstName: "First name",
      lastName: "Last name",
      name: "Name",
      contactName: "Contact",
      email: "Email",
      phone: "Phone",
      siret: "SIRET",
      vatNumber: "VAT number",
      chorusServiceCode: "Chorus Pro service code",
      paymentTermsDays: "Payment terms (days)",
      line1: "Address",
      line2: "Address line 2",
      postalCode: "Postal code",
      city: "City",
      country: "Country",
      website: "Website",
      currency: "Currency",
      leadTimeDays: "Lead time (days)",
      notes: "Notes",
      sku: "SKU",
      title: "Description",
      productType: "Category",
      ean: "EAN",
      weightGrams: "Weight (g)",
      salePrice: "Sale price excl. VAT",
      salePriceGross: "Sale price incl. VAT",
      cost: "Purchase cost excl. VAT",
      vatRate: "VAT (%)",
      reorderPoint: "Reorder point",
      targetStock: "Target stock",
      supplier: "Main supplier",
      initialStock: "Opening stock",
      newSku: "New reference",
      composition: "Composition",
      docKind: "Type (quote, invoice, credit note)",
      docNumber: "Number",
      docDate: "Date",
      customer: "Customer",
      net: "Net total",
      vat: "VAT",
      gross: "Gross total",
      paid: "Amount paid",
      status: "Status",
    },
    run: "Import {count, plural, one {# row} other {# rows}}",
    done: "{created, plural, =0 {Nothing created} one {# created} other {# created}}, {skipped, plural, =0 {no duplicates} one {# duplicate skipped} other {# duplicates skipped}}, {failed, plural, =0 {no errors} one {# error} other {# errors}}",
  },
  stockAlerts: {
    subject: "{company}: {count, plural, one {# item} other {# items}} to reorder",
    intro:
      "{count, plural, one {# item is} other {# items are}} at or under the reorder threshold (open purchase orders included):",
    noSupplier: "No main supplier",
    line: "  • {sku} {label} — available {available}, on order {incoming}, threshold {threshold} → order {suggested}",
    cta: "Prepare the purchase orders: {url}",
  },
  printing: {
    sent: "Sent to printer “{printer}” ({agent}).",
    labelAuto: "The label comes straight out of the printer.",
    kinds: { label: "Label", shipment_delivery_note: "Delivery note", order_delivery_note: "Delivery note (preparation)" },
    statuses: { pending: "Queued", printing: "Printing", done: "Printed", failed: "Failed" },
    agents: {
      title: "Printing computers",
      description:
        "A small program (the agent) runs on the computer the printer is attached to and prints what the ERP sends it, with no dialog and no click. One computer = one agent.",
      empty: "No computer is connected yet.",
      add: "Connect a computer",
      addDescription: "Give it a telling name; you will then get the token to enter on that computer.",
      create: "Create the token",
      name: "Computer name",
      nameHint: "For instance “Warehouse PC” or “Yannick's Mac”.",
      download: "Download the agent",
      downloadHint: "One file, nothing to install: put it on the computer the printer is attached to.",
      platforms: {
        windows: "Windows",
        "macos-apple-silicon": "macOS (Apple Silicon, M1 and later)",
        "macos-intel": "macOS (Intel)",
        linux: "Linux",
      },
      online: "Online",
      offline: "Offline",
      printers: "Printers: {list}",
      noPrinter: "The agent sees no printer.",
      neverSeen: "The agent never connected.",
      lastSeen: "seen on {date}",
      removed: "Computer removed.",
      removeTitle: "Remove “{name}”?",
      removeBody: "Its token stops working at once and its queued prints are dropped.",
    },
    token: {
      title: "Token of “{name}”",
      description: "To do once on the computer the printer is attached to.",
      step1:
        "Download the print agent below (one file, nothing to install) and put it on that computer, for instance in Documents.",
      step2: "Open a terminal (macOS: Terminal; Windows: PowerShell) and run this command:",
      step3: "Then run “erp-print-agent”: it stays open and the computer shows “Online” here within seconds.",
      step4: "Choose below the printer for labels and the one for delivery notes, then save.",
      onceWarning: "This token is shown only once. If it is lost, remove the computer and connect it again.",
      copied: "Copied",
      copyFailed: "Could not copy: select the command and copy it by hand.",
    },
    routes: {
      title: "Where to print",
      description:
        "For each document, the computer and printer that output it with no click. “Browser” keeps the usual print dialog. When the computer is offline at shipping time, the ERP falls back to the browser.",
      label: "Carrier labels",
      deliveryNote: "Delivery notes",
      printer: "Printer",
      browser: "Browser (print dialog)",
      pickPrinter: "Pick a printer",
      noPrinterYet: "The agent has not reported any printer yet: start it on the computer, the list fills by itself.",
      noAgent: "Connect a computer above first.",
    },
    jobs: {
      title: "Latest prints",
      description: "What was sent to the printers, with the outcome. A failure is retried three times, then reported in Health.",
      empty: "Nothing has been printed by an agent yet.",
    },
  },
  settings: {
    title: "Settings",
    groups: {
      account: "My account",
      company: "Company",
      sales: "Sales",
      catalog: "Catalogue and stock",
      purchasing: "Purchasing",
      logistics: "Logistics and after-sales",
      admin: "Administration",
    },
    hub: {
      search: "Search a setting…",
      noResult: "No setting matches “{query}”.",
      switchTo: "Go to another setting",
      summaries: {
        account: "Your name, password, two-step verification, theme and language.",
        company: "Legal name, contact details, bank and logo shown on every document.",
        documents: "Layout and texts of quotes, invoices, credit notes and delivery notes.",
        sales: "Defaults of quotes and invoices, deposits, sending to the accountant.",
        families: "Product families: order, renaming and merging.",
        stock: "Serial number tracking of articles.",
        purchasing: "Bank accounts of supplier payments.",
        shipping: "Boxes, average weights per family and default La Poste format.",
        printing: "Click-free printing of labels and delivery notes.",
        sav: "After-sales reasons and alert delay of open cases.",
        users: "Accounts, roles and access to the ERP.",
        integrations: "Shopify, e-mail, carriers, accountant, IABAKO, AI assistant.",
        imports: "Take-over of customers, products, quotes and invoices; IABAKO sync.",
        activity: "Who changed what, and when.",
        health: "Status of jobs, integrations and consistency checks.",
      },
    },
    pages: {
      account: { title: "Profile and security" },
      company: {
        title: "Identity",
        subtitle: "Company name, contact details, bank and logo: printed on quotes, invoices, purchase orders and the Factur-X.",
      },
      documents: { title: "Documents" },
      sales: {
        title: "Quotes and invoices",
        subtitle: "Defaults of quotes and invoices, deposits, invoices of web orders and sending to the accountant.",
      },
      families: { title: "Families" },
      purchasing: { title: "Purchasing", subtitle: "Bank accounts offered when a supplier payment is entered." },
      stock: { title: "Stock", subtitle: "Following articles by serial number, from receipt to shipment." },
      shipping: {
        title: "Shipping",
        subtitle:
          "Boxes in stock, average weights per family and the default La Poste format: what each parcel is weighed and described with.",
      },
      printing: {
        title: "Printing",
        subtitle:
          "Labels and delivery notes coming out of the printer with no click, through an agent installed on the computer the printer is attached to.",
      },
      sav: {
        title: "After-sales",
        subtitle: "Reasons offered when a case is opened, and the delay after which an open case is flagged.",
      },
      users: { title: "Users" },
      integrations: { title: "Integrations" },
      imports: { title: "Imports" },
      activity: { title: "Activity log", subtitle: "The latest changes made in the ERP, by every user." },
      health: { title: "Health" },
    },
    salesSections: { quotes: "Quotes", invoices: "Invoices and credit notes", deposits: "Deposits", orders: "Orders" },
    savAlert: "Alert",
    paymentAccounts: "Payment accounts",
    paymentAccountsHint:
      "The accounts supplier payments go out from (bank, card…). The first one is offered by default; each payment keeps the account used.",
    paymentAccountsEmpty: "No account: the bank is typed freely on each payment.",
    paymentAccount: "Account",
    paymentAccountDefault: "Default account",
    paymentAccountPlaceholder: "Qonto, bank card…",
    paymentAccountAdd: "Add an account",
    documentsSubtitle:
      "Layout and default texts of quotes, invoices, credit notes, delivery notes and purchase orders. The preview updates with every change.",
    sections: "Settings sections",
    readOnly: "Only an administrator can change these settings.",
    webInvoiceModes: {
      external: "Issued in Shopify (Simple Invoice)",
      erp: "Issued by the ERP automatically",
      manual: "Issued by the ERP by hand",
    },
    webInvoiceModeHints: {
      external:
        "The invoice printed by Simple Invoice is the one that counts: the ERP records it under the Shopify order number as soon as the order is paid, with no FAC number and no PDF. Shopify refunds add a credit note to it.",
      erp: "Every paid web order is invoiced, issued and marked as paid automatically, with a FAC number.",
      manual: "Nothing is automatic: every website order is invoiced from its page, like the others.",
    },
    autoReplenishHint:
      "Keeps one draft purchase order per main supplier up to date with the products below their alert threshold. Nothing is sent automatically.",
    replenishTitle: "Restocking",
    creditNoteEngagementHint:
      "When unchecked, a credit note does not repeat the invoice's commitment number (neither on the PDF nor in Factur-X). The purchase order number stays.",
    savReasons: "After-sales reasons",
    savReturnAddress: "Return address",
    savExchangeEmailTitle: "Return labels",
    savExchangeEmailHint:
      "On an exchange the return label travels in the new item's parcel; it can also be e-mailed to the customer. Every e-mailed label can be copied to the company's address to print it straight away.",
    savExchangeEmail: "Also e-mail the return label to the customer on an exchange",
    savLabelCopy: "Copy return labels to the company's e-mail address",
    savReturnAddressHint:
      "Addressee of return labels (for instance the Chambly post office where you collect parcels). Empty: the company's address. Changeable parcel by parcel.",
    savReasonsHint:
      "Reasons offered when a case is opened, on top of the built-in ones. Each opens an after-sales case, an exchange or a return.",
    savReasonsBuiltIn: "Built-in reasons: Defect, Damaged in transit, Wrong item, Size exchange, Withdrawal, Other.",
    savReasonLabel: "Reason",
    savReasonNew: "reason",
    savReasonAdd: "Add a reason",
    serialTrackingHint:
      "Checked, every receipt, shipment, exit, after-sales return or inventory of a tracked product requires one number per unit; units are found with ⌘K and on the article page. Unchecked, nothing is asked and recorded numbers stay readable. A tracked product with stock but no numbers must be filled in from its page first.",
    depositInvoicesHint:
      "Checked, quote acceptance and the order page offer a deposit invoice (type 386). The final invoice takes the paid deposit off: its taxable base and VAT cover the balance due, with the deposit's reference (DGFiP use cases 20-21). Tell the accountant before enabling.",
    marginAlertHint: "Quotes whose margin falls below this threshold are flagged.",
    savAlertHint: "Beyond that, the case shows under “To watch” on the dashboard.",
    documentTemplate: {
      scope: "Settings for",
      scopeHint: '"All documents": the common settings. One document may have its own header, table and blocks, and its texts.',
      scopeCommon: "All documents",
      ownSetting: "Own setting: {document}",
      afterTotal: "Text after the total",
      afterTotalHint: "Printed right under the totals (lead times, specific conditions…).",
      identity: "Visual identity",
      identityHint: "Logo, colours and fonts of every document.",
      logoSize: "Logo size",
      logoSizes: { sm: "Small", md: "Medium", lg: "Large" },
      accent: "Title colour",
      accentHint: "Trade name and document title. Navy by default.",
      textColor: "Text colour",
      mutedColor: "Secondary text colour",
      colorDefault: "Default",
      headingFont: "Heading font",
      bodyFont: "Body font",
      fontHint: "Two fonts are bundled: Inter (plain) and Jost (geometric).",
      letterhead: "Letterhead",
      letterheadHint: "Contact details printed under the trade name. The values are set in Settings › Identity.",
      showEmail: "E-mail",
      showPhone: "Phone",
      showWebsite: "Website",
      showSiren: "SIREN in the footer",
      table: "Lines table",
      tableHint: "Columns of the table on quotes, invoices and credit notes.",
      sku: "Item reference",
      skuModes: { inline: "Under the description", column: "Own column", hidden: "Hidden" },
      discount: "Discount column",
      discountModes: { auto: "When a line is discounted", always: "Always", never: "Never" },
      vat: "VAT column",
      description: "Description under the label",
      blocks: "Bottom of the document",
      blocksHint: "Order and presence of the blocks printed under the totals.",
      blockNames: {
        notes: "Document notes",
        payment: "Payment terms (legal notices, IBAN)",
        terms: "Special conditions",
        closing: "Closing line",
        signature: "Acceptance box (quotes)",
      },
      quote: "Quote options",
      showIban: "Remind the IBAN on quotes",
      signature: "Acceptance box on quotes",
      texts: "Default texts",
      textsHint: "Used when the document has none of its own. An empty text is not printed.",
      textsForLabel: "Document texts",
      textsFor: {
        quote: "Quotes",
        invoice: "Invoices",
        creditNote: "Credit notes",
        deliveryNote: "Delivery notes",
        purchaseOrder: "Purchase order (French)",
        purchaseOrderEn: "Purchase order (English)",
        quoteEn: "Quotes (English)",
        invoiceEn: "Invoices (English)",
        creditNoteEn: "Credit notes (English)",
        deliveryNoteEn: "Delivery notes (English)",
      },
      englishTextsHint: "Printed for a customer who reads English. An English text left empty takes the French one.",
      creditNoteTextsHint: "A text left empty takes the invoices' one.",
      deliveryTermsHint: "Printed under the table of items.",
      intro: "Introduction",
      introHint: "Printed before the table, after the subject.",
      terms: "Special conditions",
      closing: "Closing line",
      footer: "Footer",
      footerHint: "Extra notices at the bottom of every page; replaces the shared footer.",
      sharedFooter: "Shared footer",
      sharedFooterEn: "Shared footer (English)",
      sharedFooterEnHint: "For a customer or supplier who reads English; empty = the French footer.",
      sharedFooterHint: "On every document, under the legal line: late payment penalties, recovery fee…",
      preview: "Preview",
      previewKind: "Document shown",
      openPreview: "Open the preview",
      previewError: "The preview could not be generated.",
      moveUp: "Move up",
      moveDown: "Move down",
      show: "Print this block",
    },
    audit: "Activity log",
    profile: "My profile",
    role: "Role",
    preferences: "Preferences",
    password: {
      title: "Password",
      description: "Choose a personal password you do not use anywhere else.",
      change: "Change password",
      rules: "At least 12 characters. An easy-to-remember passphrase works well.",
      current: "Current password",
      new: "New password",
      confirm: "Confirm the new password",
      revokeOthers: "Sign out my other devices",
      wrongCurrent: "The current password is incorrect.",
      changed: "Password changed",
    },
    twoFactor: {
      title: "Two-step verification",
      description: "A code from your phone is required when signing in on a new device.",
      enabled: "On",
      disabled: "Off",
      enable: "Turn on",
      disable: "Turn off",
      password: "Confirm your password",
      scan: "Scan this QR code with your authenticator app (Google Authenticator, 1Password…), then enter the code it shows.",
      manualKey: "Key for manual entry",
      confirm: "Confirm",
      enabledToast: "Two-step verification turned on",
      disabledToast: "Two-step verification turned off",
      backupCodes: "Backup codes",
      backupHint: "Keep these codes somewhere safe. Each one lets you sign in if you lose access to your phone.",
      done: "I saved my codes",
    },
  },
  integrations: {
    title: "Integrations",
    subtitle: "Connect the ERP to your services. Passwords and tokens are encrypted and never shown again once saved.",
    active: "Connected",
    missing: "To set up",
    save: "Save",
    saved: "Settings saved",
    test: "Test connection",
    testOk: "Connection successful",
    testOkDetail: "Connection successful: {detail}",
    testEmailTo: "Also send a test email to {email}",
    secretSet: "Saved",
    secretEmpty: "Not set",
    secretReplace: "Replace",
    secretRemove: "Remove",
    secretKeep: "Keep current",
    fromServer: "Value set on the server; a value entered here overrides it.",
    optional: "Optional",
    unsaved: "Unsaved changes",
    howTo: "Where do I find this?",
    lastSync: "Last synced {date}",
    neverSynced: "Never synced",
    failedWebhooks: "{count, plural, one {# failed Shopify notification} other {# failed Shopify notifications}}",
    syncNow: "Sync now",
    synced:
      "{orders, plural, =0 {No orders to fetch} one {# order synced} other {# orders synced}}, {linked, plural, =0 {stock already linked} one {# item linked to Shopify stock} other {# items linked to Shopify stock}}",
    webhookUrl: "Webhook receiving address",
    copy: "Copy",
    copied: "Address copied",
    registerWebhooks: "Subscribe Shopify to orders",
    webhooksRegistered: "{created, plural, =0 {Webhooks already in place} one {# webhook created} other {# webhooks created}}",
    locations: "Shopify locations",
    useLocation: "Use",
    providers: {
      shopify: {
        name: "Shopify",
        description: "Web orders, stock, tracking numbers and catalog import.",
        howTo:
          "In Shopify admin: Settings › Apps › Develop apps › Create an app. Admin API access: read/write_orders, read/write_inventory, read_products, read_locations, read/write_merchant_managed_fulfillment_orders. After installing, copy the Admin API token (shpat_…) and the API secret key (for webhooks).",
      },
      email: {
        name: "Email sending",
        description: "Quotes, invoices and purchase orders sent from the ERP.",
        howTo:
          "Your mailbox SMTP settings. Google Workspace: smtp.gmail.com, port 465 encrypted, username = full address, app password. OVH: ssl0.ovh.net, port 465.",
      },
      chronopost: {
        name: "Chronopost",
        description: "Chrono 13 (or other product) labels and parcel tracking.",
        howTo:
          "Chronopost customer account number (8 digits) and Web Services password, from your Chronopost sales contact. Chronopost parcel tracking works even without an account.",
      },
      colissimo: {
        name: "Colissimo",
        description: "Outbound, return and reshipment labels.",
        howTo:
          "You need a Colissimo contract (Facilité or higher) with Web Services enabled: call 3634 or ask your La Poste contact. Then, in the Colissimo Box: edit your profile › “Web Services connection keys” › Generate, and paste the key here with the customer account number (6 digits, next to the company name). The password is only for older setups, without a key.",
      },
      laposte: {
        name: "La Poste — parcel tracking",
        description:
          "Delivery detected automatically for Colissimo parcels and tracked letters, without a Colissimo contract (labels made in La Poste Pro Expédition, numbers typed in or received from Shopify).",
        howTo:
          'Create a free account on developer.laposte.fr, then an application (My applications › Create an application). Subscribe it to the "Suivi" API (free plan) and copy the production key shown on the application. Shipped parcels are checked every two hours.',
      },
      chorus: {
        name: "Chorus Pro",
        description: "Automatic filing of invoices addressed to public bodies.",
        howTo:
          "On piste.gouv.fr: create an application and subscribe it to the Chorus Pro API (OAuth client id and secret). On chorus-pro.gouv.fr: create a technical account linked to the SIRET. Start in qualification mode.",
      },
      openai: {
        name: "AI assistant (OpenAI)",
        description: "The ERP assistant: answers questions about the software and your data, and prepares actions you confirm.",
        howTo:
          "Create a key on platform.openai.com (API keys) and paste it here; it is encrypted and never shown again. The model is optional (gpt-5 by default). Questions and the data the assistant reads are sent to OpenAI.",
      },
      iabako: {
        name: "IABAKO (previous tool)",
        description: "Takes over the quotes and sales created in IABAKO until the switch-over, from Settings › Imports.",
        howTo:
          "The IABAKO account the ERP signs in with to read quotes and sales. The ERP only looks: it never saves anything in IABAKO. « Test connection » opens then closes a session.",
      },
      inqom: {
        name: "Accountant (Inqom)",
        description: "Every issued invoice and credit note is forwarded automatically, through the Inqom API or by e-mail.",
        howTo:
          "Two options. By e-mail: the sales invoice collection address provided by Inqom or by the firm. Through the API (direct deposit in the dossier, with tracking): ask the firm for an Inqom user dedicated to the ERP, the API client secret and the dossier id (the number in Inqom's address when the dossier is open). Start in the sandbox, then untick it. Once the API is configured, it replaces the e-mail.",
      },
    },
    fields: {
      shopify: {
        shopDomain: { label: "Shop domain", hint: "The .myshopify.com address" },
        clientId: { label: "App client ID", hint: "Shopify Dev Dashboard › App settings" },
        webhookSecret: {
          label: "App secret key",
          hint: "Starts with shpss_; used to get the access token and to verify webhooks",
        },
        adminToken: { label: "Admin API token (legacy app)", hint: "Starts with shpat_; overrides the client ID when set" },
        locationId: { label: "Shopify location", hint: "Leave empty to use the primary location" },
        apiVersion: { label: "API version", hint: "Keep the default unless needed" },
        publicUrl: { label: "Website address", hint: "Used to import the catalog" },
        writeBack: {
          label: "Write to Shopify",
          hint: "Sends stock, shipments (with an e-mail to the customer), after-sales refunds and exchange notes to the shop. Enable only in production, once initial stock is entered",
        },
        syncExchanges: {
          label: "Report after-sales exchanges on the Shopify order",
          hint: "Once an exchange is sent: a line in the order note and the “echange-sav” tag. Line items, payment and the Shopify invoice do not change. Needs “Write to Shopify” and the write_orders access",
        },
        pushPrices: {
          label: "Send prices to Shopify",
          hint: "A sale price changed in the ERP (product card or import) is sent to the Shopify variant, VAT included. Only the price is written. Independent of “Write to Shopify”. When off, the changed price waits and the ERP keeps it. Needs the write_products access",
        },
      },
      email: {
        host: { label: "SMTP server", hint: "" },
        port: { label: "Port", hint: "465 (encrypted) or 587 (STARTTLS)" },
        secure: { label: "Encrypted connection (SSL/TLS)", hint: "Tick for port 465" },
        user: { label: "Username", hint: "" },
        password: { label: "Password", hint: "" },
        fromName: { label: "Sender name", hint: "" },
        fromEmail: { label: "Sender address", hint: "" },
        replyTo: { label: "Reply-to address", hint: "Where customers' replies land. Defaults to the company e-mail" },
      },
      chronopost: {
        accountNumber: { label: "Account number", hint: "8 digits" },
        password: { label: "Web Services password", hint: "" },
        productCode: { label: "Product", hint: "01 Chrono 13 (default), 02 Chrono 10, 16 Chrono 18" },
      },
      colissimo: {
        apiKey: {
          label: "API key (Colissimo Box › my profile)",
          hint: "“Web Services connection keys” when editing the profile. Replaces the password",
        },
        contractNumber: {
          label: "Customer account number",
          hint: "The 6 digits shown next to the company name in the Colissimo Box. With the API key, Colissimo requires it on some accounts (error 30008)",
        },
        password: { label: "Web Services password", hint: "Former method: not needed when the API key is set" },
      },
      laposte: {
        okapiKey: { label: "API key (X-Okapi-Key)", hint: "The application's production key, on developer.laposte.fr" },
      },
      chorus: {
        clientId: { label: "PISTE OAuth client id", hint: "" },
        clientSecret: { label: "PISTE OAuth secret", hint: "" },
        techLogin: { label: "Technical account login", hint: "" },
        techPassword: { label: "Technical account password", hint: "" },
        sandbox: { label: "Qualification mode (tests)", hint: "Untick to file real invoices" },
      },
      openai: {
        apiKey: { label: "OpenAI API key", hint: "" },
        model: { label: "Model", hint: "gpt-5 by default" },
      },
      iabako: {
        email: { label: "IABAKO sign-in e-mail", hint: "" },
        password: { label: "IABAKO password", hint: "" },
      },
      inqom: {
        collectionEmail: { label: "Collection address (e-mail)", hint: "Also editable in Settings › Quotes and invoices" },
        sandbox: { label: "Inqom sandbox (tests)", hint: "Untick to deposit in the real dossier" },
        username: { label: "Inqom user (API)", hint: "Sign-in address of the dedicated user" },
        password: { label: "Inqom password", hint: "" },
        clientId: { label: "API client ID", hint: "Given by Inqom with the secret; “Web” by default" },
        clientSecret: { label: "API client secret", hint: "Provided by Inqom" },
        enterpriseId: { label: "Dossier id", hint: "Number, visible in Inqom's address" },
      },
    },
  },
  users: {
    title: "Users",
    subtitle: "Accounts are created here; nobody can sign up on their own.",
    new: "New user",
    edit: "Edit user",
    empty: "No users",
    you: "You",
    fields: {
      name: "Name",
      email: "Email",
      role: "Role",
      password: "Temporary password",
      status: "Access",
      lastSeen: "Last activity",
      twoFactor: "Two-step verification",
    },
    passwordHint: "At least 12 characters. Share it through a safe channel; the user will change it afterwards.",
    generate: "Generate",
    statuses: {
      active: "Active",
      disabled: "Disabled",
    },
    never: "Never signed in",
    roleHints: {
      admin: "Full access, including settings and users.",
      sales: "Customers, products, quotes, orders, invoices and after-sales, imports and Shopify sync. Read access to the rest.",
      logistics: "Stock, shipments, purchasing, suppliers, products and after-sales. Read access to customers and orders.",
      accounting: "Read access to everything and exports, with no changes.",
    },
    actions: {
      edit: "Edit",
      disable: "Disable access",
      enable: "Restore access",
      resetPassword: "Set a password",
    },
    resetTitle: "New password for {name}",
    resetHint: "Their open sessions will be closed.",
    disableTitle: "Disable {name}'s access?",
    disableBody: "Their sessions are closed immediately. Their activity history is kept and access can be restored.",
    created: "User created",
    updated: "User updated",
    disabled: "Access disabled",
    enabled: "Access restored",
    passwordSet: "Password updated",
    copied: "Password copied",
  },
  integrity: {
    title: "System health",
    subtitle:
      "Automatic checks of the rules that must always hold: numbering, amounts, stock. They run every day from 6 a.m.; administrators get an e-mail at the slightest discrepancy.",
    run: "Run the check",
    running: "Checking…",
    lastRun: "Last check on {date}",
    never: "No check has run yet.",
    allGood: "No inconsistency: all {count, number} rules hold.",
    issues: "{count, plural, one {# rule broken} other {# rules broken}}",
    occurrences: "{count, plural, one {# case} other {# cases}}",
    worker: "Background tasks",
    workerOk: "Running (last heartbeat on {date})",
    workerStale: "Stopped since {date}: Shopify sync, e-mails and parcel tracking are paused.",
    workerNever: "Never started.",
    backup: {
      title: "Backups",
      ok: "Test restore succeeded on {date}: {count, plural, one {# issued invoice} other {# issued invoices}} found.",
      failed: "Test restore failed on {date}: {detail}",
      never: "No test restore yet: it runs every Sunday morning.",
    },
    errors: {
      title: "Unexpected errors (last 30 days)",
      empty: "No unexpected error.",
      seen: "{count, plural, one {# time} other {# times}}, last on {date}",
      sources: { api: "Server", web: "Browser", job: "Background task" },
    },
    errorDigest: {
      subject: "{company}: {count, plural, one {# unexpected error} other {# unexpected errors}} in the ERP",
      intro:
        "{count, plural, one {# unexpected error happened} other {# unexpected errors happened}} since the last message. Please forward this message to support.",
      line: "  • [{source}] {message} — {where} ({count, plural, one {# time} other {# times}})",
    },
    email: {
      subject: "{company}: {count, plural, one {# inconsistency} other {# inconsistencies}} found in the ERP",
      intro:
        "Last night's check found {count, plural, one {# broken rule} other {# broken rules}}. Nothing was changed; please forward this message to support.",
      check: "{check} ({count, plural, one {# case} other {# cases}}):",
      cta: "Details: {url}",
    },
    checks: {
      jobs_not_stuck_running: "No background job “running” for more than 30 minutes",
      jobs_not_left_waiting: "No background job still waiting an hour after its due time",
      invoice_numbering_gapless: "Invoice and credit note numbers with no gap or duplicate",
      invoice_numbering_matches_sequence: "Every number handed out belongs to an issued document",
      invoice_number_matches_date: "Number prefix and year match the document and its date",
      issued_invoice_has_number_and_date: "Issued documents numbered and dated, drafts unnumbered",
      invoice_paid_matches_payments: "Amount paid equals the sum of payments",
      invoice_not_overpaid: "No invoice paid beyond its amount",
      payments_only_on_issued_invoices: "Payments only on issued documents",
      credit_notes_within_invoice: "Credit notes never exceed the original invoice",
      credit_lines_within_billed: "No invoice line credited beyond the quantity billed",
      credit_note_corrects_an_issued_invoice: "Every credit note corrects an issued invoice",
      order_line_quantities_in_range: "Shipped and invoiced quantities within ordered ones",
      order_allocations_on_open_orders: "Stock set aside only on orders to process or in preparation",
      order_kit_lines_match_shopify_line: "Lines of a split Shopify kit: whole sets, the same number on each article",
      order_line_invoiced_matches_invoices: "Invoiced quantities equal invoice lines",
      order_line_billed_within_quantity: "No order line billed beyond its quantity, credit notes deducted",
      order_line_shipped_matches_parcels: "Shipped quantities equal parcel contents",
      delivered_order_has_delivered_parcels: "Delivered orders have no pending parcel",
      shipped_parcel_left_stock_once: "Each parcel left stock exactly once",
      cancelled_parcel_back_in_stock: "Each cancelled parcel gave back to stock exactly what it took",
      stock_movement_direction: "Stock movement directions and costs are consistent, including the trace of compositions",
      kit_has_no_stock_movements: "No movement nor stock of its own on a composition",
      kit_components_are_not_kits: "The components of a composition are physical articles",
      quote_has_one_order: "Each accepted quote became exactly one order",
      archived_article_has_no_stock: "No stock left on an archived article",
      purchase_line_received_in_range: "Received quantities within purchase orders",
      sav_line_received_in_range: "After-sales receipts between zero and the declared quantity",
      sav_returns_within_received: "No more units back from the customer than received on the order (replacements included)",
      purchase_received_matches_receipts: "Quantities received on purchase orders match stock entries",
      purchase_closed_has_invoice: "Purchase order closed only with its invoice, « invoice to file » only without",
      order_allocations_within_remaining: "Units set aside within what is left to ship",
      order_archived_only_when_finished: "Only delivered or cancelled orders are archived",
      cancelled_count_has_no_movements: "A cancelled stock count booked nothing",
      resent_parcel_not_cancelled: "A resent parcel is not cancelled while its resend stands",
      sav_credit_note_of_same_order: "After-sales credit notes belong to the right order",
      invoice_refs_match_order:
        "Invoices carry the commitment and order numbers of their order, credit notes those of their invoice",
      no_job_failed_for_a_day: "No background task failing for more than a day",
      shopify_order_not_archived: "No Shopify order of the CSV history also taken over as an ERP order",
      order_totals_match_lines: "Stored order totals equal those of their lines",
      external_document_of_shopify_order:
        "Documents issued in Shopify belong to a website order, are issued, have no PDF, and credit notes share their invoice's issuer",
      external_invoice_unique_per_order: "A single Shopify invoice per website order",
      order_not_invoiced_twice: "No order invoiced both in Shopify and in the ERP",
      inqom_deposit_recorded: "Documents deposited at Inqom carry their id and transmission date",
      purchase_document_deposit_recorded: "Purchase documents deposited at Inqom carry their id and transmission date",
      invoice_totals_match_lines: "Invoice totals match their lines",
      quote_totals_match_lines: "Quote totals match their lines",
      deposit_deducted_within_amount: "Deposits taken off at most once, within their amount",
      deposit_deducted_only_when_paid: "Only issued and paid deposits are taken off a final invoice",
      deposits_within_order: "Deposits of an order within its amount",
      deposit_has_order: "Every deposit invoice is linked to an order, without order lines",
      serial_units_match_stock: "Units in stock equal to the physical stock of serial-tracked articles",
      serial_shipment_lines_complete: "Parcels of tracked articles with one serial number per unit",
      serial_status_consistent: "Unit statuses consistent with their documents",
    },
  },
  audit: {
    title: "Activity log",
    history: "History",
    changedFields: "{count, plural, one {# field changed} other {# fields changed}}",
    emptyTitle: "No activity",
    actions: {
      create: "{actor} created {entity}",
      update: "{actor} updated {entity}",
      archive: "{actor} archived {entity}",
      restore: "{actor} restored {entity}",
      import: "{actor} imported {entity}",
      delete: "{actor} deleted {entity}",
      cancel: "{actor} cancelled {entity}",
    },
    system: "System",
  },
  confirms: {
    handDelivery: {
      title: "Deliver this order yourself?",
      body: "Everything left to ship leaves the stock, without label or carrier, and the order becomes « Delivered ». A web shop order is also fulfilled in Shopify.",
      confirm: "Delivered myself",
    },
    unsaved: {
      title: "Leave without saving?",
      body: "The changes made in this form will be lost.",
      confirm: "Leave without saving",
    },
    quoteInvoice: {
      title: "Invoice this quote?",
      body: 'The quote becomes "Accepted": an order is created (stock is reserved) and a draft invoice is prepared. The quote can no longer be edited.',
      confirm: "Invoice",
    },
    quoteDecline: {
      title: "Mark this quote declined?",
      body: "The quote is closed: it can no longer be accepted or edited. To pick it up again, duplicate it.",
      confirm: "Mark declined",
    },
    quoteCancel: {
      title: "Cancel this quote?",
      body: "The quote is closed: it can no longer be accepted or edited. To pick it up again, duplicate it.",
      confirm: "Cancel quote",
    },
    orderDelivered: {
      title: "Mark the order delivered?",
      body: 'The order becomes "Delivered" without waiting for carrier tracking, and the archiving delay starts. This cannot be undone.',
      confirm: "Mark delivered",
    },
    shipmentDelivered: {
      title: "Mark this parcel delivered?",
      body: 'The parcel becomes "Delivered" without waiting for carrier tracking. If it was the order\'s last parcel, the order becomes "Delivered" too. This cannot be undone.',
      confirm: "Mark delivered",
    },
    orderFileRemove: {
      title: "Remove this file?",
      body: '"{name}" is deleted from the order and will not be attached to invoices filed on Chorus Pro from now on.',
      confirm: "Remove",
    },
    paymentsClear: {
      title: "Set back to awaiting payment?",
      body: "The recorded payments ({amount}) are removed: the whole invoice is due again. They remain in the audit log.",
      confirm: "Set back to awaiting",
    },
    paymentRemove: {
      title: "Delete this payment?",
      body: "The {amount} payment is removed: the balance due goes up by the same amount.",
      confirm: "Delete payment",
    },
    chorusResubmit: {
      title: "Deposit on Chorus Pro again?",
      body: "The issued invoice is deposited as is, with its attachments. If it was rejected, first fix what caused the rejection (service code, commitment number); otherwise issue a credit note and a new invoice.",
      confirm: "Deposit again",
    },
    savResolve: {
      title: "Resolve this case?",
      repair: 'The case becomes "Resolved" as repaired. No stock movement, no refund. You can still reopen it.',
      none: 'The case becomes "Resolved" with no further action. No stock movement, no refund. You can still reopen it.',
      confirm: "Resolve",
    },
    iabakoSync: {
      title: "Take over these IABAKO documents?",
      body: "{count, plural, one {# document will be created or updated} other {# documents will be created or updated}} in the ERP, exactly as previewed. They count in revenue. Nothing is sent to the customer or the accountant.",
      confirm: "Apply",
    },
    shopifySync: {
      title: "Sync now?",
      body: "The ERP reads the Shopify orders changed since the last sync right away: new orders are created, stock is reserved, invoices follow the web invoicing mode. This also runs on its own every 15 minutes.",
      confirm: "Sync",
    },
    catalogImport: {
      title: "Import the Shopify catalog?",
      body: "Web products are created or updated: title, family, description, options, weight and price are overwritten from Shopify. ERP SKUs, thresholds, costs, suppliers and compositions are not touched.",
      confirm: "Import",
    },
    attachVariant: {
      title: "Attach the variant?",
      body: "The variant leaves its product for the chosen one, with its stock and history.",
      confirm: "Attach",
    },
    splitVariants: {
      title: "Split the variants?",
      body: "The items are created, the stock on hand moves to them and each web variant becomes a composition. This cannot be undone.",
      confirm: "Split",
    },
    userRole: {
      title: "Change {name}'s role?",
      body: "The new role ({role}) applies immediately and the person is signed out of all their devices.",
      confirm: "Change role",
    },
    stockExit: {
      title: "Remove from stock?",
      body: '{count, plural, one {# unit leaves} other {# units leave}} stock with no parcel, at the current average cost. A mistake is fixed with "Correct stock".',
      confirm: "Remove from stock",
    },
    inventoryCancel: {
      title: "Cancel this stock count?",
      body: "The counted quantities are dropped and nothing is written to stock. A cancelled count cannot be resumed: you will have to start a new one.",
      confirm: "Cancel count",
    },
    serialDisable: {
      title: "Stop tracking this product by serial number?",
      body: "Receipts, shipments and returns will no longer ask for numbers for this product. Numbers already recorded stay in the history.",
      confirm: "Turn off tracking",
    },
    typeTrackingOn: {
      title: 'Track every "{type}" product?',
      body: "Every receipt, shipment and return of these {count, plural, one {# product} other {# products}} will ask for serial numbers. Nothing changes if one of them cannot be tracked (a kit component, for example); the message names that product.",
      confirm: "Track the whole type",
    },
    typeTrackingOff: {
      title: 'Stop tracking "{type}" products?',
      body: "These {count, plural, one {# product} other {# products}} will no longer ask for serial numbers. Numbers already recorded stay in the history.",
      confirm: "Stop tracking",
    },
    logoRemove: {
      title: "Go back to the Phygiatech logo?",
      body: "The uploaded logo is deleted: upcoming quotes, invoices and purchase orders will carry the Phygiatech logo. Documents already issued do not change. To get it back, upload the file again.",
      confirm: "Back to the Phygiatech logo",
    },
  },
  assistant: {
    title: "Assistant",
    open: "Open the assistant",
    bubbleHint: "Assistant — click to open, drag to move",
    description: "Ask about the software or your data, or request an action.",
    keys: "Enter to send · Shift + Enter for a new line",
    placeholder: "E.g. “Which quotes have been waiting for more than 15 days?”",
    send: "Send",
    newConversation: "New conversation",
    history: "Conversations",
    untitled: "Untitled",
    thinking: "The assistant is thinking…",
    empty: "Ask a question or request an action. For example:",
    examples: {
      overdue: "Which invoices are overdue?",
      toShip: "Which orders have been waiting for more than 3 days?",
      stock: "What is the stock of the Loreca vests, size by size?",
      howTo: "How do I issue a partial credit note?",
    },
    notConfigured: "The assistant is not configured yet: an administrator must enter the OpenAI key in Settings › Integrations.",
    privacy: "Questions and the data read are sent to OpenAI.",
    guide: "Guide read: {query}",
    read: "Read",
    pendingTitle: "Action to confirm",
    pendingHint: "Nothing changes until you click “Run”. The action is done with your rights and logged.",
    details: "Technical detail",
    execute: "Run",
    refuse: "Decline",
    outcomes: { pending: "Waiting", done: "Done", failed: "Failed", refused: "Declined" },
  },
  help: {
    ui: {
      title: "Help",
      subtitle: "How the ERP works, workflow by workflow: what each action does, what it changes and what leaves the ERP.",
      search: "Search the help (e.g. refund, label, deposit)",
      noResult: "Nothing matches.",
      contents: "On this page",
      about: "What this action does",
      more: "Learn more ›",
    },
    quote: {
      preview: "Draws the PDF with the current values, even unsaved, with a “Preview” watermark. Nothing is saved or sent.",
      send: "E-mails the quote PDF to the customer. Once sent, the quote becomes “Sent” and its PDF is frozen. A sent e-mail cannot be recalled.",
      accept:
        "Turns the quote into an order: the order is created, stock is reserved and the quote becomes “Accepted” for good. Can record the references (purchase order, Chorus) and create a deposit.",
      invoice:
        "First accepts the quote if needed (order created, stock reserved, no way back), then prepares a draft invoice. Nothing is issued until you click “Issue”.",
      duplicate: "Creates a new numbered draft quote with the same lines, dated today. The original does not change.",
      decline:
        "Closes the quote for good (declined or cancelled). It can no longer be accepted or edited; duplicate it to start again.",
    },
    order: {
      allocate:
        "Sets the stock of the ticked lines aside for this order, before any other (even older or urgent). The order turns « Ready » once everything is covered. Set-aside units are used when shipping, freed on cancellation. Reversible (« Release »).",
      prepare: "Opens the delivery note to print and moves the order to “Preparing”. Nothing leaves stock at this step.",
      deliveryNote: "PDF of the lines still to ship, to print twice (one left, one signed). Nothing is recorded.",
      urgent: "Puts the order at the top of the lists and serves it first from available stock. Can be undone at any time.",
      delivered:
        "Marks the order “Delivered” without touching its parcels. Normally automatic from carrier tracking; starts the archiving delay. Cannot be undone.",
      cancel:
        "Cancels the order and releases the reserved stock. Cancels nothing in Shopify, nor existing invoices or deposits. Cannot be undone.",
      archive: "Moves the order to “Archived” (done automatically after the set delay). Can be undone.",
      invoice:
        "Prepares a draft invoice with what is not invoiced yet (shipping on the first invoice). The order leaves the “To invoice” view. Deleting the draft undoes everything.",
      deposit:
        "Prepares a draft deposit invoice (a percentage or an amount), one line per VAT rate. Numbered when issued, then deducted from the final invoice.",
      publicRefs:
        "Commitment number, SIRET, service code, purchase order and signed delivery note: copied onto draft invoices and attached to the Factur-X sent to Chorus Pro. Locked once an invoice or deposit is issued.",
    },
    invoice: {
      issue:
        "Gives the final gapless number, freezes the Factur-X PDF, then sends it to the accountant (Inqom or e-mail) and to Chorus Pro for a public customer. Cannot be undone: correct with a credit note.",
      deleteDraft: "Deletes the draft and gives the “to invoice” quantities back to the order. Impossible once issued.",
      payment:
        "Records a payment received (amount pre-filled with the balance). Updates the invoice's paid amount and the order's payment status. A payment can be deleted.",
      creditNote:
        "Prepares a draft credit note, whole or by line, to issue afterwards. Puts nothing back in stock and refunds nobody: it is an accounting document.",
      send: "E-mails the issued PDF to the customer. Changes no status.",
      pdf: "Draft: preview with a watermark. Issued invoice: the archived PDF, always identical. Shopify invoice: a copy rebuilt by the ERP, never the original.",
      export: "Downloads a ZIP of the PDFs issued in the period, with a CSV summary (credit notes negative). Nothing is changed.",
      external:
        "Invoice issued by Shopify (Simple Invoice): the ERP records it with its payment but cannot issue, edit or send it.",
    },
    customer: {
      importShopify:
        "Reads every customer of the shop. Known ones (same id or e-mail) are linked without overwriting what was typed in the ERP; the others are created.",
      archive: "Hides the customer from lists and pickers. Their documents stay and their web orders keep coming. Can be undone.",
      history:
        "Quotes and invoices taken over from previous tools (IABAKO, Shopify orders older than 60 days), read-only. They count in report revenue.",
    },
    shopify: {
      openOrder: "Opens the order in the Shopify admin, in a new tab.",
      sync: "Reads the Shopify orders changed since the last sync (also run every 15 min): creates or updates orders, customers, shipments made in Shopify and, depending on the chosen mode, records or issues invoices.",
      webhooks:
        "Asks Shopify to notify the ERP whenever an order is created, paid, edited, cancelled or refunded. Writes to the shop's configuration.",
      writeBack:
        "Lets the ERP write to Shopify: available stock, shipments (with an e-mail to the customer), after-sales refunds and exchange notes. Off, the ERP only reads.",
      webInvoiceMode:
        "Who invoices web orders: Shopify (the ERP only records), the ERP automatically on each payment (numbered invoice, paid, sent to the accountant), or by hand.",
    },
    shipment: {
      ship: "Creates the shipment and the carrier label if asked, takes the items out of stock and updates the order. For a Shopify order only these lines are marked shipped and the customer gets the tracking. Can be cancelled until delivered.",
      laposteFile:
        "File to import into La Poste Pro Expédition to print labels. Creates no shipment and takes nothing out of stock: the Shopify tracking records them later.",
      parcelLaposteFile:
        "La Poste file for this parcel (one row per box). Careful: if a Colissimo or Chronopost label already exists, you will get a second one.",
      delivered:
        "Marks the parcel “Delivered” (normally automatic from tracking). When all parcels of an order are delivered, so is the order. Cannot be undone.",
      tracking:
        "Saves the tracking number. For a web order it is sent to Shopify (added to the existing fulfillment if there is one) and Shopify emails the tracking to the customer again.",
      resend:
        "Sends the same content again in a new parcel (lost, damaged): items leave stock again, the order does not change. Closes no after-sales case.",
      serials:
        "Fixes the serial numbers that left in this parcel: removed ones come back to stock, added ones leave. Creates no stock movement.",
      cancel:
        "Cancels a shipment not yet delivered: items come back to stock and the order goes back to preparation. The carrier label is not cancelled. Impossible once the shipment is in Shopify.",
      free: "Ships items without an order (sample, loan, fair): delivery note, label, stock out. The customer is not linked to the parcel.",
    },
    stock: {
      adjust:
        "Replaces the quantity in stock with the counted one (at the given date) by booking the difference as a movement. For a tracked article, enter the added or missing serial numbers.",
      exit: "Takes items out of stock without a parcel (breakage, loss, internal use, loan), with a required reason. Refused if stock is short. Undone with “Correct stock”.",
      count:
        "Creates a draft stock count with every article (or one family) at the chosen date. Stock does not change until it is validated.",
      countValidate:
        "Books, at the count date, one movement per gap between counted and expected. Cannot be undone; a gap is fixed with a correction.",
      countCancel: "Drops the count and everything typed. Stock does not change.",
      serials:
        "Each unit carries a number, followed from receipt to shipment. Turning tracking on needs one number per unit in stock; turning it off keeps the history.",
      composition: "The article sold is a set (vest + cummerbund): it has no stock of its own, its components enter and leave.",
    },
    sav: {
      undo: "Reverts the case's last label, parcel, receipt, resolution or closing. Items sent come back into stock, items received leave it again. A refund is not undone.",
      returnLabel:
        "Creates a Colissimo return label (PDF to pass on to the customer, the ERP does not send it). Nothing leaves stock; the case waits for the parcel.",
      laposteReturn:
        "File to import into La Poste Pro Expédition to print the return label (parcel addressed to the company). The case waits for the return.",
      receive:
        "Records the items received and their condition: “Restocked” puts them back in stock; quarantine and scrap stay out of sellable stock.",
      replace:
        "Sends the replacement items right away (stock out, delivery note, label) without waiting for the return, and resolves the case.",
      exchange:
        "Sends the new item and creates the return label for the old one, slipped into the parcel. The case then waits for the return, checked on receipt.",
      refund:
        "Shopify order: refunds the customer in Shopify (if writing is on) and the credit note is created when the refund arrives. Otherwise: a draft credit note on the invoice. Puts nothing back in stock.",
      resolve:
        "Marks the case resolved (repaired, or no action) without sending or refunding anything. It still has to be closed.",
      close: "Closes the case: no more actions, except reopening it.",
      reopen: "Reopens the closed case; it becomes “Open” again with its history.",
    },
    purchase: {
      close:
        "Ends the reception: what is missing is no longer expected in stock nor owed to the supplier. The order then waits for its invoice (“Invoice to file”); once filed in the documents, it goes to the accountant and the order is archived. Cannot be undone.",
      send: "E-mails the purchase order PDF to the supplier; once sent, the order becomes “Sent”. Subject and message are in French, the PDF in the supplier's language.",
      status:
        "Changes the status without sending anything: “Sent” dates the order, “Confirmed” records the supplier's agreement. Quantities then count as “incoming”. No way back.",
      edit: "Editable until the reception is closed, never below what was already received. Nothing is resent automatically: remember to send the new version to the supplier.",
      receive:
        "Puts the received quantities into stock at the cost converted to euros at the rate entered, and recomputes the average cost. Can file the supplier invoice. Cannot be undone.",
      payment:
        "Records a deposit, balance or transport paid to the supplier, with the bank used; the amount still owed is recomputed.",
      cancel: "Cancels the order: quantities are no longer expected. The supplier is not told; payments and documents stay.",
      suggestions:
        "Articles under their threshold, incoming stock included. “Create order” prepares one draft per supplier; nothing is sent.",
      documents:
        "Documents dropped here (invoices, credit notes, receipts, contracts) are kept and sent to the accountant (Inqom or e-mail) right away, with their type.",
      creditNote: "Supplier credit note, optional: its amount is deducted from what is still owed on the linked order.",
      transmit:
        "Sends the accountant the documents not sent yet (for instance after setting up Inqom). A transmitted document can no longer be deleted.",
    },
    product: {
      importShopify:
        "Reads the shop's catalogue: creates new products and updates title, family, options, weight and price (VAT-inclusive converted to net). SKU, thresholds, costs and suppliers stay the ERP's.",
      split:
        "Creates one article per option (vest by size, cummerbund by size) and turns each site variant into a composition; stock moves to the articles. Cannot be undone.",
      archive:
        "Hides the product from lists and pickers. The Shopify product is untouched and stock keeps syncing. Can be undone.",
      families:
        "Renaming or merging changes the family of every product concerned, archived ones included; parcel weights and formats follow. A merge cannot be undone.",
      webLocked:
        "Title, family, description, options and weight come from Shopify and are rewritten at every catalogue import. The price belongs to the ERP: changed here, it is sent to Shopify (when sending prices is on).",
    },
    accounting: {
      chorus:
        "Public customers' invoices filed on Chorus Pro (Factur-X with purchase order and signed delivery note); the status is read back hourly. “Resubmit” sends a rejected invoice again.",
      inqom:
        "Every issued invoice and purchase document goes to the accountant: through the Inqom API when configured (it then replaces e-mail), otherwise to the collection address.",
      inqomSandbox:
        "In sandbox, documents go to a test dossier; they are marked sent and will not go to the real dossier afterwards.",
      deposits:
        "Turns deposit invoices on: offered when a quote is accepted and from the order, then deducted from the final invoice.",
    },
    auto: {
      jobs: "Automatic tasks: Shopify sync (15 min), parcel tracking (2 h), order archiving, Chorus statuses and error digest (hourly), stock alerts and integrity check (daily).",
    },
    settings: {
      integrationTest:
        "Checks the connection with the saved credentials. For some services the test only proves authentication, not actual sending.",
      archiveDelay:
        "Delivered or cancelled orders move to “Archived” after this delay (checked hourly). They can still be viewed and restored.",
      userRole: "Changing the role immediately changes what the person can see and do, and signs them out of every device.",
      import:
        "“Check” validates the file without writing; “Import” creates or updates according to the duplicate mode. An import cannot be undone in bulk.",
      health:
        "Runs the data consistency checks (stock, invoices, payments…). They also run daily, with an e-mail to administrators on any anomaly.",
      printing:
        "An agent installed on the computer the printer is attached to prints labels and delivery notes with no click. When offline, the ERP falls back to the browser's print dialog.",
    },
    statuses: {
      all: "What each status means (orders, invoices, shipments, after-sales, purchasing, counts) and what changes it.",
    },
  },
};
